/**
 * ToolResults
 *
 * Rich, normalized renderers for tool activity entries. Instead of
 * dumping raw JSON, this file maps NormalizedToolResult kinds to
 * small interactive components that mirror the style of existing
 * annotations like WebSearch.
 *
 * Primary responsibilities:
 * - Take a ToolActivityEntry and produce React elements
 * - Use normalizeToolResult to convert raw JSON into stable shapes
 * - Keep per-tool rendering factored into focused components
 *
 * Dependencies:
 * - lib/tool-results/normalize-tool-result
 * - components/messages/annotations/websearch
 */

'use client'

import React, { useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  Archive,
  BookOpenCheck,
  Bug,
  CalendarDays,
  CheckCircle2,
  CheckSquare,
  Clipboard,
  Download,
  ExternalLink,
  FileCode,
  FileImage,
  FileText,
  Flag,
  FolderOpen,
  Gauge,
  Hand,
  Link as LinkIcon,
  Loader2,
  MessageCircle,
  MonitorDot,
  Palette,
  Paperclip,
  PlayCircle,
  Radio,
  ShieldAlert,
  Smartphone,
  Table2,
  Upload,
  Video,
  Users
} from 'lucide-react'
import {
  IconCpu,
  IconDeviceLaptop,
  IconLayoutDashboard,
  IconSparkle2
} from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TextareaAutosize } from '@/components/ui/textarea-autosize'
import { WebSearch as WebSearchAnnotation } from '@/components/messages/annotations/websearch'
import { LiveScoreAnnotation } from '@/components/messages/annotations/live-score'
import { ImageWithPreview } from '@/components/image/image-with-preview'
import { AnimatedMarkdownVideo } from '@/components/messages/animated-markdown-video'
import { getSafeRenderableImageUrl } from '@/lib/media/safe-url'
import type { Annotation } from '@/types/annotation'
import {
  McpSetupCardView,
  getMcpSetupCard
} from '@/components/integrations/mcp-setup-card'
import {
  SkillSetupCardView,
  getSkillSetupCard
} from '@/components/integrations/skill-setup-card'
import { ExtensionConnectionCard } from '@/components/extensions/extension-connection-card'
import { getComposioToolkitDisplayName } from '@/lib/services/composio-auth-setup-card'
import { isSoftGateToolResult } from '@/lib/chat/prior-tool-results-context'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { ChatbotUIContext } from '@/context/context'
import { stripMarkdownForPreview } from '@/lib/strip-markdown-for-preview'
import type { Tables } from '@/types/supabase'
import {
  createCredentialFieldsFromGeneration,
  getMissingRequiredCredentialFields,
  resolveHeadersForSave,
  resolveSchemaForSave,
  type HeaderRow,
  type IntegrationCredentialField,
  type IntegrationGenerationResponse
} from '@/components/sidebar/items/tools/custom-integration-form-fields'
import { SharingField } from '@/components/sidebar/items/all/sharing-field'
import {
  type NormalizedToolResult,
  type FetchedUrlNormalizedResult,
  type VirtualFileSingleNormalizedResult,
  type VirtualFileListNormalizedResult,
  type WebSearchNormalizedResult,
  type WeatherNormalizedResult,
  type FinanceQuoteNormalizedResult,
  type TaskNormalizedResult,
  type MemoryNormalizedResult,
  type ChatManagementNormalizedResult,
  type ConnectedServicesNormalizedResult,
  type PdfDocumentNormalizedResult,
  type ToolManagementNormalizedResult,
  type VectorMemoryNormalizedResult,
  type SearchNormalizedResult,
  type MediaGenerationNormalizedResult,
  type SidecarRunNormalizedResult,
  type SpacesConversationMessagesNormalizedResult,
  type SpacesEntityNormalizedResult,
  type SpacesEntitySummary,
  type SpacesEntityType,
  type SpacesInvitationNormalizedResult,
  type SpacesStatusNormalizedResult,
  type SpacesTaskSupportNormalizedResult,
  normalizeToolResult
} from '@/lib/tool-results/normalize-tool-result'
import { MINI_IDE_WORKSPACE_EVENT } from '@/lib/chat/mini-ide-workspace'
import {
  COMPUTER_INTERACTION_ATTRIBUTE,
  markComputerInteraction
} from '@/lib/chat/computer-interaction-focus'
import {
  getSandboxFileLinks,
  isPdfSandboxFile
} from '@/lib/platformTools/sandboxFileArtifacts'
import type { ToolActivityEntry } from '@/components/messages/annotations/tool-activity-log'
import { SidecarRunAnnotation } from '@/components/messages/annotations/sidecar-run'
import {
  Sandbox,
  SandboxContent,
  SandboxHeader,
  SandboxTabContent,
  SandboxTabs,
  SandboxTabsBar,
  SandboxTabsList,
  SandboxTabsTrigger,
  type SandboxState
} from '@/components/ai-elements/sandbox'
import { CodeBlock } from '@/components/ai-elements/code-block'
import { Terminal } from '@/components/ai-elements/terminal'
import {
  FileTree,
  FileTreeFile,
  FileTreeFolder
} from '@/components/ai-elements/file-tree'
import { StackTrace } from '@/components/ai-elements/stack-trace'
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep
} from '@/components/ai-elements/chain-of-thought'

interface ToolResultRendererProps {
  entry: ToolActivityEntry
}

// --------------

function openInNewTab(url: string) {
  if (!url) return
  try {
    window.open(url, '_blank', 'noopener,noreferrer')
  } catch {
    // ignore
  }
}

function openMiniIdeWorkspace(detail: Record<string, unknown>) {
  window.dispatchEvent(
    new CustomEvent(MINI_IDE_WORKSPACE_EVENT, {
      cancelable: true,
      detail
    })
  )
}

function redactUrlCredentials(url: string | null) {
  if (!url) return null

  try {
    const parsed = new URL(url)
    if (!parsed.host) return url
    return `${parsed.protocol}//${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return url
      .replace(/(^[a-z][a-z0-9+.-]*:\/\/)[^/?#@]+@/i, '$1')
      .replace(/^(\/\/)[^/?#@]+@/, '$1')
  }
}

function isInfrastructureRouteUrl(url: string | null) {
  if (!url) return false

  try {
    const parsed = new URL(url)
    return parsed.hostname.endsWith('.vercel.run')
  } catch {
    return /\.vercel\.run(?:[/:?#]|$)/i.test(url)
  }
}

function getComputerRouteDisplayLabel(url: string | null, fallback: string) {
  if (!url) return null
  if (isInfrastructureRouteUrl(url)) return fallback

  const safeUrl = redactUrlCredentials(url)
  if (!safeUrl) return null

  try {
    const parsed = new URL(safeUrl)
    parsed.search = ''
    parsed.hash = ''
    return parsed.toString()
  } catch {
    return safeUrl
  }
}

function redactProxyCredentials(url: string | null) {
  if (!url) return null

  return getSanitizedNetworkRouteHost(url) ?? 'proxy configured'
}

// --------------

function WebSearchResultCard({ result }: { result: WebSearchNormalizedResult }) {
  // `result.raw` can be a single run or an array of runs depending on how the
  // caller stored tool results. Render each run as its own card so consecutive
  // searches do not collapse into the last one.
  // normalizeToolResult/safeGetResult already unwraps JSON strings + `{ result }`.
  const runs = useMemo(() => {
    const raw = result.raw as unknown
    return Array.isArray(raw) ? raw : raw ? [raw] : []
  }, [result.raw])

  if (runs.length === 0) return null

  return (
    <div className="space-y-3">
      {runs.map((runRaw, index) => {
        const run = runRaw as any
        const annotation = { webScraper__googleSearch: runRaw as any }

        const query =
          typeof run?.query === 'string'
            ? run.query
            : typeof result.query === 'string'
              ? result.query
              : null

        const totalResults =
          typeof run?.totalResults === 'number'
            ? run.totalResults
            : typeof (run?.searchInformation?.totalResults as any) === 'number'
              ? run.searchInformation.totalResults
              : typeof result.totalResults === 'number'
                ? result.totalResults
                : null

        return (
          <div key={`websearch-run-${index}`} className="space-y-2">
            {(query || totalResults != null) && (
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <div className="truncate">
                  <span className="font-medium">Web search</span>
                  {query && (
                    <>
                      {' '}
                      <span className="font-mono">&quot;{query}&quot;</span>
                    </>
                  )}
                </div>
                {typeof totalResults === 'number' && (
                  <span className="whitespace-nowrap">{totalResults.toLocaleString()} results</span>
                )}
              </div>
            )}
            <WebSearchAnnotation annotation={annotation as any} />
          </div>
        )
      })}
    </div>
  )
}

// --------------

function parseRecordResult(result: unknown): Record<string, unknown> | null {
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    return result as Record<string, unknown>
  }

  if (typeof result !== 'string') {
    return null
  }

  try {
    const parsed = JSON.parse(result) as unknown
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

function parseToolRow(result: unknown): Tables<'tools'> | null {
  const record = parseRecordResult(result)

  if (
    !record ||
    typeof record.id !== 'string' ||
    typeof record.name !== 'string' ||
    record.schema === undefined
  ) {
    return null
  }

  return record as Tables<'tools'>
}

function upsertToolRow(tools: Tables<'tools'>[], tool: Tables<'tools'>) {
  if (tools.some((item) => item.id === tool.id)) {
    return tools.map((item) => (item.id === tool.id ? tool : item))
  }

  return [tool, ...tools]
}

const generatedIntegrationCredentialCache = new Map<string, Record<string, string>>()

function getGeneratedIntegrationCredentialCacheKey(draft: GeneratedIntegrationDraft) {
  return JSON.stringify({
    headers: draft.custom_headers || {},
    name: draft.name,
    schema: draft.schema,
    userInput: draft.user_input || ''
  })
}

function mergeCachedCredentialValues(cacheKey: string, fields: IntegrationCredentialField[]) {
  const cachedValues = generatedIntegrationCredentialCache.get(cacheKey)

  if (!cachedValues) return fields

  return fields.map((field) => ({
    ...field,
    value: cachedValues[field.id] ?? cachedValues[field.key] ?? field.value
  }))
}

function cacheCredentialValues(cacheKey: string, fields: IntegrationCredentialField[]) {
  generatedIntegrationCredentialCache.set(
    cacheKey,
    Object.fromEntries(
      fields.flatMap((field) => [
        [field.id, field.value],
        [field.key, field.value]
      ])
    )
  )
}

type GeneratedIntegrationDraft = IntegrationGenerationResponse & {
  auth_options?: unknown
  auth_strategy?: unknown
  custom_headers?: Record<string, string>
  environment_fields?: unknown
  sharing?: string
  tool_id?: string
  toolId?: string
  url?: string
  user_input?: string
}

function getToolAnnotationResult(annotation: Record<string, unknown>, key: string): unknown {
  const entry = annotation[key]
  const record = parseRecordResult(entry)

  return record && 'result' in record ? record.result : entry
}

function getStringField(record: Record<string, unknown>, key: string): string {
  const value = record[key]

  return typeof value === 'string' ? value : ''
}

function getStringArrayField(record: Record<string, unknown>, key: string): string[] {
  const value = record[key]

  return Array.isArray(value)
    ? value.flatMap((item) => (typeof item === 'string' && item.trim() ? [item.trim()] : []))
    : []
}

function getContentEngineNestedRecord(record: Record<string, unknown>, key: string) {
  return parseRecordResult(record[key]) || {}
}

function getContentEngineNestedArray(record: Record<string, unknown>, key: string) {
  const value = record[key]
  return Array.isArray(value) ? value : []
}

function getContentEngineSummaryRows(record: Record<string, unknown>) {
  const campaign = getContentEngineNestedRecord(record, 'campaign')
  const output = getContentEngineNestedRecord(record, 'outputRecord')
  const note = getContentEngineNestedRecord(record, 'note')
  const task = getContentEngineNestedRecord(record, 'task')
  const generatedAssets = getContentEngineNestedArray(record, 'generatedAssets')
  const failedLabels = getStringArrayField(record, 'failedLabels')

  return [
    campaign.id
      ? {
          label: 'Campaign',
          value: getStringField(campaign, 'name') || getStringField(campaign, 'topic')
        }
      : null,
    campaign.status ? { label: 'Campaign status', value: getStringField(campaign, 'status') } : null,
    output.id
      ? {
          label: 'Asset pack',
          value: getStringField(output, 'name') || getStringField(output, 'sourceCampaignName')
        }
      : null,
    output.status ? { label: 'Asset status', value: getStringField(output, 'status') } : null,
    generatedAssets.length
      ? { label: 'Generated visuals', value: `${generatedAssets.length}` }
      : null,
    failedLabels.length ? { label: 'Failed visuals', value: failedLabels.join(', ') } : null,
    note.id ? { label: 'Space note', value: getStringField(note, 'title') } : null,
    task.id ? { label: 'Space task', value: getStringField(task, 'title') } : null
  ].filter((row): row is { label: string; value: string } => Boolean(row?.value))
}

function ContentEngineResultCard({
  entry
}: {
  entry: ToolActivityEntry
}) {
  const record = parseRecordResult(entry.result)

  if (!record) {
    return null
  }

  const success = record.success === true
  const error = getStringField(record, 'error')
  const stage = getStringField(record, 'stage')
  const requiresConfirmation = record.requiresConfirmation === true
  const requiresContent = record.requiresContent === true
  const rows = getContentEngineSummaryRows(record)

  return (
    <Card className="border-border/70 bg-background p-3 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-md">
          <IconCpu className="size-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-semibold">Content Engine</div>
            <Badge variant={success ? 'secondary' : 'outline'}>
              {requiresConfirmation
                ? 'Needs approval'
                : requiresContent
                  ? 'Needs asset pack'
                  : success
                    ? 'Saved'
                    : 'Attention needed'}
            </Badge>
            {stage && <Badge variant="outline">{stage.replace(/_/g, ' ')}</Badge>}
          </div>

          {error ? (
            <div className="text-destructive flex items-center gap-2 text-xs">
              <AlertCircle className="size-3.5" />
              <span>{error}</span>
            </div>
          ) : (
            <p className="text-muted-foreground text-xs">
              {requiresContent
                ? 'Content Engine needs a complete asset pack payload before this stage can continue.'
                : requiresConfirmation
                  ? 'This action is waiting for approval in the message.'
                  : 'Content Engine saved this stage.'}
            </p>
          )}

          <ChainOfThought defaultOpen>
            <ChainOfThoughtHeader>Engine stages</ChainOfThoughtHeader>
            <ChainOfThoughtContent>
              <ChainOfThoughtStep
                icon={CheckCircle2}
                label="Campaign draft"
                status={stage === 'campaign_draft_review' ? 'active' : 'complete'}
              />
              <ChainOfThoughtStep
                icon={FileText}
                label="Asset pack"
                status={
                  stage === 'asset_pack_review'
                    ? 'active'
                    : stage === 'visuals_generated' || stage === 'linked_to_space'
                      ? 'complete'
                      : 'pending'
                }
              />
              <ChainOfThoughtStep
                icon={Palette}
                label="Visuals and handoff"
                status={
                  stage === 'visuals_generated' || stage === 'linked_to_space'
                    ? 'complete'
                    : 'pending'
                }
              />
            </ChainOfThoughtContent>
          </ChainOfThought>

          {rows.length > 0 && (
            <div className="grid gap-1.5 text-xs sm:grid-cols-2">
              {rows.map((row) => (
                <div key={row.label} className="rounded-md border border-border/60 bg-muted/30 p-2">
                  <div className="text-muted-foreground">{row.label}</div>
                  <div className="truncate font-medium">{row.value}</div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </Card>
  )
}

function getGeneratedIntegrationDraft(result: unknown): GeneratedIntegrationDraft | null {
  const record = parseRecordResult(result)
  const draft = record ? parseRecordResult(record.draft) || record : null

  if (!record || !draft || !draft.schema) {
    return null
  }

  const name = getStringField(draft, 'name') || 'Generated integration draft'
  const description = getStringField(draft, 'description')
  const customHeaders = parseRecordResult(draft.custom_headers)

  return {
    auth_options: draft.auth_options,
    auth_strategy: getStringField(draft, 'auth_strategy') || 'unknown',
    custom_headers: Object.fromEntries(
      Object.entries(customHeaders || {}).flatMap(([key, value]) =>
        typeof value === 'string' ? [[key, value]] : []
      )
    ),
    description,
    environment_fields: draft.environment_fields,
    name,
    schema: draft.schema,
    sharing: getStringField(draft, 'sharing') || getStringField(record, 'sharing') || 'private',
    tool_id:
      getStringField(draft, 'tool_id') ||
      getStringField(record, 'tool_id') ||
      getStringField(draft, 'toolId') ||
      getStringField(record, 'toolId'),
    url: getStringField(draft, 'url'),
    user_input: getStringField(draft, 'user_input')
  }
}

function getGeneratedIntegrationEndpointCount(schema: unknown): number {
  const schemaRecord = parseRecordResult(schema)
  const paths = schemaRecord ? parseRecordResult(schemaRecord.paths) : null

  return paths ? Object.keys(paths).length : 0
}

function getSchemaServerUrl(schema: unknown): string {
  const schemaRecord = parseRecordResult(schema)
  const servers = schemaRecord?.servers

  if (!Array.isArray(servers)) return ''

  const server = servers.map(parseRecordResult).find((item) => item !== null)

  return getStringField(server || {}, 'url')
}

function createHeaderRows(headers: Record<string, string>): HeaderRow[] {
  return Object.entries(headers).map(([name, value], index) => ({
    id: `${name}-${index}`,
    name,
    value
  }))
}

function getGeneratedIntegrationAuthOptions(draft: GeneratedIntegrationDraft) {
  return parseRecordResult(draft.auth_options)
}

function createGeneratedIntegrationDraftFromGeneration(
  result: unknown,
  userInput: string,
  previousDraft?: GeneratedIntegrationDraft
): GeneratedIntegrationDraft | null {
  const record = parseRecordResult(result)

  if (!record) return null

  return getGeneratedIntegrationDraft({
    draft: {
      ...record,
      sharing: previousDraft?.sharing || getStringField(record, 'sharing') || 'private',
      tool_id: previousDraft?.tool_id || previousDraft?.toolId,
      user_input: userInput
    }
  })
}

function buildGeneratedIntegrationIterationPrompt(
  draft: GeneratedIntegrationDraft,
  feedback: string
) {
  return [
    draft.user_input || `Create a custom integration for ${draft.name}`,
    `Revise the previous generated integration draft with this feedback:\n${feedback.trim()}`,
    'Return a complete updated integration draft, not a patch.',
    'Keep auth, base URL, credential fields, and verification grounded in documentation.',
    `Previous draft JSON:\n${JSON.stringify(draft)}`
  ].join('\n\n')
}

async function readJsonResponse(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

function GeneratedIntegrationApprovalCard({
  draft: initialDraft
}: {
  draft: GeneratedIntegrationDraft
}) {
  const { selectedWorkspace, setTools } = useContext(ChatbotUIContext)
  const { navigateToHref } = useSplitPaneNavigation()
  const [draft, setDraft] = useState(initialDraft)
  const draftCacheKey = useMemo(() => getGeneratedIntegrationCredentialCacheKey(draft), [draft])
  const [fields, setFields] = useState<IntegrationCredentialField[]>(() =>
    mergeCachedCredentialValues(
      getGeneratedIntegrationCredentialCacheKey(initialDraft),
      createCredentialFieldsFromGeneration(initialDraft)
    )
  )
  const [feedback, setFeedback] = useState('')
  const [regenerating, setRegenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [createdToolId, setCreatedToolId] = useState<string | null>(null)
  const [sharing, setSharing] = useState(initialDraft.sharing || 'private')
  const authOptions = getGeneratedIntegrationAuthOptions(draft)
  const evidence = authOptions ? getStringArrayField(authOptions, 'documentation_evidence') : []
  const uncertainty = authOptions ? getStringArrayField(authOptions, 'uncertainty_flags') : []
  const verification = authOptions
    ? getStringArrayField(authOptions, 'verification_instructions')
    : []
  const baseUrl =
    authOptions && typeof authOptions.base_url === 'string' ? authOptions.base_url : draft.url || ''
  const endpointCount = getGeneratedIntegrationEndpointCount(draft.schema)
  const missingRequiredFields = getMissingRequiredCredentialFields(fields)
  const targetToolId = draft.tool_id || draft.toolId || ''
  const isUpdateDraft = Boolean(targetToolId)
  const actionLabel = isUpdateDraft ? 'Update' : 'Create'
  const actionPastLabel = isUpdateDraft ? 'Updated' : 'Created'
  const createDisabled = saving || Boolean(createdToolId) || missingRequiredFields.length > 0

  function updateCredentialFields(
    updater: (currentFields: IntegrationCredentialField[]) => IntegrationCredentialField[]
  ) {
    setFields((currentFields) => {
      const nextFields = updater(currentFields)
      cacheCredentialValues(draftCacheKey, nextFields)
      return nextFields
    })
  }

  async function regenerateDraft() {
    if (!feedback.trim()) {
      toast.error('Tell Losi what to change first')
      return
    }

    setRegenerating(true)

    try {
      const currentDraft = { ...draft, sharing }
      const userInput = buildGeneratedIntegrationIterationPrompt(currentDraft, feedback)
      const { runGeneratePluginRequest } = await import(
        '@/lib/services/run-generate-plugin-client'
      )
      const data = await runGeneratePluginRequest(authenticatedFetch, userInput)
      const nextDraft = createGeneratedIntegrationDraftFromGeneration(data, userInput, currentDraft)

      if (!nextDraft) {
        throw new Error('The updated draft was invalid')
      }

      const currentValues = new Map(
        fields.flatMap((field) => [
          [field.id, field.value],
          [field.key, field.value]
        ])
      )
      const nextFields = createCredentialFieldsFromGeneration(nextDraft).map((field) => ({
        ...field,
        value: currentValues.get(field.id) ?? currentValues.get(field.key) ?? field.value
      }))

      setDraft(nextDraft)
      setSharing(nextDraft.sharing || sharing)
      setFields(nextFields)
      cacheCredentialValues(getGeneratedIntegrationCredentialCacheKey(nextDraft), nextFields)
      generatedIntegrationCredentialCache.delete(draftCacheKey)
      setCreatedToolId(null)
      setFeedback('')
      toast.success('Draft updated')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update draft')
    } finally {
      setRegenerating(false)
    }
  }

  async function saveIntegration() {
    if (createdToolId) {
      return
    }

    const missing = getMissingRequiredCredentialFields(fields)

    if (missing.length > 0) {
      const labels = missing.map((field) => field.label).join(', ')
      toast.error(`Enter required credentials: ${labels}`)
      return
    }

    setSaving(true)

    try {
      const schemaForSave = resolveSchemaForSave(draft.schema, fields)
      const response = await authenticatedFetch('/api/chat/marshmallow/generated-integration', {
        body: JSON.stringify({
          authOptions: draft.auth_options,
          authStrategy: draft.auth_strategy,
          customHeaders: resolveHeadersForSave(
            createHeaderRows(draft.custom_headers || {}),
            fields
          ),
          description: draft.description,
          draft: false,
          environmentFields: fields.map((field) => ({
            description: field.description,
            hasSavedValue: field.hasSavedValue,
            headerName: field.headerName,
            key: field.key,
            label: field.label,
            placeholder: field.placeholder,
            required: field.required,
            secret: field.secret,
            value: field.value,
            valueTemplate: field.valueTemplate,
            vaultSecretId: field.vaultSecretId
          })),
          name: draft.name,
          schema: schemaForSave,
          sharing,
          toolId: targetToolId || undefined,
          url: getSchemaServerUrl(schemaForSave) || baseUrl,
          userInput: draft.user_input,
          workspaceId: sharing === 'workspace' ? selectedWorkspace?.id : undefined
        }),
        headers: { 'Content-Type': 'application/json' },
        method: isUpdateDraft ? 'PATCH' : 'POST'
      })

      const data = await readJsonResponse(response)
      const dataRecord = parseRecordResult(data)
      const tool = dataRecord ? parseToolRow(dataRecord.tool) : null

      if (!response.ok) {
        throw new Error(
          getStringField(dataRecord || {}, 'error') ||
            `Failed to ${isUpdateDraft ? 'update' : 'create'} integration`
        )
      }

      if (tool) {
        setTools((current) => upsertToolRow(current, tool))
      }

      generatedIntegrationCredentialCache.delete(draftCacheKey)
      setCreatedToolId(tool?.id || 'created')
      toast.success(`Integration ${isUpdateDraft ? 'updated' : 'created'}`)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to ${isUpdateDraft ? 'update' : 'create'} integration`
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="border border-border/70 bg-background p-3 shadow-sm">
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <IconSparkle2 className="size-4 shrink-0 text-primary" />
              <h3 className="truncate text-sm font-semibold">{draft.name}</h3>
            </div>
            {draft.description ? (
              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{draft.description}</p>
            ) : null}
          </div>
          <Badge variant={createdToolId ? 'default' : 'outline'} className="shrink-0 text-[10px]">
            {createdToolId ? actionPastLabel : isUpdateDraft ? 'Update draft' : 'Draft'}
          </Badge>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary" className="text-[10px]">
            {draft.auth_strategy || 'unknown auth'}
          </Badge>
          <Badge variant="secondary" className="text-[10px]">
            {endpointCount} endpoints
          </Badge>
          {baseUrl ? (
            <Badge variant="outline" className="max-w-full truncate text-[10px]">
              {baseUrl}
            </Badge>
          ) : null}
        </div>

        {!createdToolId ? (
          <div className="rounded-md border border-border/60 px-2 pb-2">
            <SharingField value={sharing} onChange={setSharing} />
          </div>
        ) : null}

        {fields.length > 0 ? (
          <div className="grid gap-2">
            {fields.map((field) => (
              <div key={field.id} className="grid gap-1">
                <Label className="text-[11px] font-medium">
                  {field.label}
                  {field.required ? <span className="text-destructive"> *</span> : null}
                </Label>
                <Input
                  value={field.value}
                  type={field.secret ? 'password' : 'text'}
                  autoComplete={field.secret ? 'new-password' : 'off'}
                  placeholder={
                    field.hasSavedValue && field.secret
                      ? 'Saved secret - leave blank to keep it'
                      : field.placeholder || field.key
                  }
                  onChange={(event) => {
                    const nextValue = event.target.value
                    updateCredentialFields((current) =>
                      current.map((item) =>
                        item.id === field.id ? { ...item, value: nextValue } : item
                      )
                    )
                  }}
                  className="h-8 text-xs"
                />
                {field.description ? (
                  <p className="text-[10px] text-muted-foreground">{field.description}</p>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {evidence.length > 0 || uncertainty.length > 0 || verification.length > 0 ? (
          <div className="space-y-2 rounded-md border border-border/60 bg-muted/30 p-2">
            {evidence.slice(0, 2).map((item, index) => (
              <p key={`evidence-${index}`} className="text-[11px] text-muted-foreground">
                <span className="font-medium text-foreground">Evidence:</span> {item}
              </p>
            ))}
            {uncertainty.slice(0, 2).map((item, index) => (
              <p key={`uncertainty-${index}`} className="text-[11px] text-amber-700">
                <span className="font-medium">Check:</span> {item}
              </p>
            ))}
            {verification.slice(0, 1).map((item, index) => (
              <p key={`verify-${index}`} className="text-[11px] text-muted-foreground">
                <span className="font-medium text-foreground">Verify:</span> {item}
              </p>
            ))}
          </div>
        ) : null}

        {!createdToolId ? (
          <div className="grid gap-2 rounded-md border border-dashed border-border/60 p-2">
            <Label className="text-[11px] font-medium">Change request</Label>
            <TextareaAutosize
              value={feedback}
              onValueChange={setFeedback}
              minRows={2}
              maxRows={4}
              placeholder={`Tell Losi what to change before you ${actionLabel.toLowerCase()} it...`}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs"
            />
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                disabled={regenerating || !feedback.trim()}
                onClick={regenerateDraft}
              >
                {regenerating ? (
                  <Loader2 className="mr-1 size-3 animate-spin" />
                ) : (
                  <IconSparkle2 className="mr-1 size-3" />
                )}
                Apply changes
              </Button>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[11px] text-muted-foreground">
            Want changes? Add feedback here or reply in chat before saving. {actionLabel} saves it.
          </p>
          <div className="flex gap-2">
            {createdToolId ? (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => {
                  navigateToHref('/chat?sidebar=integrations')
                }}
              >
                Open Integrations
              </Button>
            ) : null}
            <Button
              size="sm"
              className="h-8 text-xs"
              disabled={createDisabled}
              onClick={saveIntegration}
            >
              {saving ? <Loader2 className="mr-1 size-3 animate-spin" /> : null}
              {actionLabel}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}

function GeneratedIntegrationCreatedCard({ result }: { result: unknown }) {
  const record = parseRecordResult(result)
  const success = record?.success !== false
  const tool = record ? parseRecordResult(record.tool) : null
  const name = tool ? getStringField(tool, 'name') : ''
  const kind = record ? getStringField(record, 'kind') : ''
  const message = record ? getStringField(record, 'message') : ''
  const successTitle =
    kind === 'generated_integration_deleted'
      ? 'Integration deleted'
      : kind === 'generated_integration_updated'
        ? 'Integration updated'
        : 'Integration created'

  return (
    <Card className="border border-border/70 bg-background p-3 shadow-sm">
      <div className="flex items-start gap-2">
        {success ? (
          <CheckCircle2 className="mt-0.5 size-4 text-emerald-600" />
        ) : (
          <AlertCircle className="mt-0.5 size-4 text-destructive" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            {success ? successTitle : 'Integration change failed'}
          </p>
          <p className="text-xs text-muted-foreground">{name || message}</p>
        </div>
      </div>
    </Card>
  )
}

export function GeneratedIntegrationDraftAnnotation({
  annotation
}: {
  annotation: Record<string, unknown>
}) {
  const createdResult =
    getToolAnnotationResult(annotation, 'GeneratedIntegrationTool__approveIntegrationDraft') ||
    getToolAnnotationResult(annotation, 'GeneratedIntegrationTool__updateIntegration') ||
    getToolAnnotationResult(annotation, 'GeneratedIntegrationTool__deleteIntegration')

  if (createdResult) {
    return <GeneratedIntegrationCreatedCard result={createdResult} />
  }

  const draftResult = getToolAnnotationResult(
    annotation,
    'GeneratedIntegrationTool__draftIntegration'
  )
  const draft = getGeneratedIntegrationDraft(draftResult)

  if (draft) {
    return <GeneratedIntegrationApprovalCard draft={draft} />
  }

  return null
}

function McpInstallResultCard({ result }: { result: unknown }) {
  const setup = getMcpSetupCard(result)

  if (!setup) {
    return null
  }

  return <McpSetupCardView compact setup={setup} />
}

function SkillInstallResultCard({ result }: { result: unknown }) {
  const setup = getSkillSetupCard(result)

  if (setup) {
    return <SkillSetupCardView compact setup={setup} />
  }

  const record = parseRecordResult(result)
  if (!record) return null

  const kind = getRecordString(record, ['kind'])?.toLowerCase() ?? ''
  const skill = getNestedRecord(record, 'skill')
  const skillName =
    getRecordString(skill, ['displayName', 'display_name', 'name']) ??
    getRecordString(record, ['message'])
  const success = record.success !== false && typeof record.error !== 'string'

  if (!success || (!kind.includes('skill_ready') && !kind.includes('skill_create'))) {
    return null
  }

  return (
    <Card className="space-y-2 border border-border/60 bg-muted/40 p-3">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background">
          <BookOpenCheck className="size-4 text-emerald-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate text-sm font-semibold">
              {skillName ? `${skillName}` : 'Skill ready'}
            </div>
            <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700" variant="outline">
              installed
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1 text-xs">
            {getRecordString(record, ['message']) ?? 'Skill is installed and ready to use.'}
          </p>
        </div>
      </div>
    </Card>
  )
}

function getRecordString(record: Record<string, unknown> | null, keys: string[]): string | null {
  if (!record) return null

  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }

  return null
}

function getPrettyJson(value: unknown) {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

type ToolSummaryRow = {
  label: string
  value: string
}

const TOOL_SUMMARY_KEYS = [
  'message',
  'error',
  'status',
  'state',
  'name',
  'title',
  'displayName',
  'operation',
  'action',
  'routerAction',
  'routedFunctionId',
  'id',
  'path',
  'publishLevel'
]

const TOOL_LINK_KEYS = new Set([
  'downloadUrl',
  'enableUrl',
  'previewUrl',
  'setupUrl',
  'sourceUrl',
  'url'
])

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function getSummaryScalar(value: unknown) {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return null
}

function summarizeNestedRecord(value: Record<string, unknown>) {
  return (
    getSummaryScalar(value.displayName) ??
    getSummaryScalar(value.name) ??
    getSummaryScalar(value.title) ??
    getSummaryScalar(value.id) ??
    null
  )
}

function summarizeToolField(key: string, value: unknown) {
  if (TOOL_LINK_KEYS.has(key) && typeof value === 'string' && value.trim()) {
    return 'Available'
  }

  const scalar = getSummaryScalar(value)
  if (scalar) return scalar

  if (Array.isArray(value)) {
    if (value.length === 0) return 'None'
    return `${value.length} item${value.length === 1 ? '' : 's'}`
  }

  if (isRecord(value)) {
    return summarizeNestedRecord(value) ?? 'Available'
  }

  return null
}

function getToolSummaryRows(value: unknown, maxRows = 8): ToolSummaryRow[] {
  if (!isRecord(value)) {
    const scalar = getSummaryScalar(value)
    return scalar ? [{ label: 'Result', value: scalar }] : []
  }

  const rows: ToolSummaryRow[] = []
  const usedKeys = new Set<string>()

  for (const key of TOOL_SUMMARY_KEYS) {
    const summary = summarizeToolField(key, value[key])
    if (!summary) continue
    rows.push({ label: key, value: summary })
    usedKeys.add(key)
    if (rows.length >= maxRows) return rows
  }

  for (const [key, fieldValue] of Object.entries(value)) {
    if (usedKeys.has(key)) continue

    const summary = summarizeToolField(key, fieldValue)
    if (!summary) continue

    rows.push({ label: key, value: summary })
    if (rows.length >= maxRows) return rows
  }

  return rows
}

function ToolPayloadSummary({
  result,
  title = 'Result summary'
}: {
  result: unknown
  title?: string
}) {
  const rows = getToolSummaryRows(result)

  if (rows.length === 0) return null

  return (
    <div className="rounded-md border border-border/60 bg-background/70 p-2 text-xs">
      <div className="mb-1 font-medium">{title}</div>
      <dl className="grid gap-1">
        {rows.map((row) => (
          <div className="grid grid-cols-[6rem_minmax(0,1fr)] gap-2" key={row.label}>
            <dt className="truncate text-muted-foreground">{row.label}</dt>
            <dd className="min-w-0 truncate font-medium">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function getMcpRuntimeTitle(entry: ToolActivityEntry, result: Record<string, unknown> | null) {
  const request = parseRecordResult(entry.request)
  const toolName = getRecordString(request, ['toolName', 'tool_name'])
  const serverName =
    getRecordString(result, ['serverName', 'server_name', 'name']) ??
    getRecordString(getNestedRecord(result, 'integration'), ['name']) ??
    getRecordString(request, ['serverName', 'server_name', 'query', 'integrationId', 'integration_id'])

  if (entry.key.endsWith('__searchMcpServers')) {
    return serverName ? `MCP search: ${serverName}` : 'MCP server search'
  }

  if (entry.key.endsWith('__installMcpServer')) {
    return serverName ? `MCP setup: ${serverName}` : 'MCP setup'
  }

  if (entry.key.endsWith('__listMcpServerTools')) {
    return serverName ? `MCP tools: ${serverName}` : 'MCP tools'
  }

  if (entry.key.endsWith('__callMcpServerTool')) {
    return [serverName, toolName].filter(Boolean).join(' / ') || 'MCP action'
  }

  return 'MCP result'
}

function McpRuntimeResultCard({ entry }: { entry: ToolActivityEntry }) {
  const result = parseRecordResult(entry.result)
  const request = parseRecordResult(entry.request)

  if (!result) return null

  const tools = Array.isArray(result.tools) ? result.tools : []
  const toolName = getRecordString(request, ['toolName', 'tool_name'])
  const serverName =
    getRecordString(result, ['serverName', 'server_name', 'name']) ??
    getRecordString(getNestedRecord(result, 'integration'), ['name']) ??
    getRecordString(request, ['serverName', 'server_name', 'query', 'integrationId', 'integration_id'])
  const success = result.success !== false && typeof result.error !== 'string'

  return (
    <Card className="space-y-3 border border-border/60 bg-muted/40 p-3">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background">
          <FileCode className="size-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate text-sm font-semibold">
              {getMcpRuntimeTitle(entry, result)}
            </div>
            <Badge
              className={success ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : ''}
              variant="outline"
            >
              {success ? 'Complete' : 'Failed'}
            </Badge>
          </div>
          <div className="mt-1 flex flex-wrap gap-1 text-[10px] text-muted-foreground">
            {serverName ? <span className="rounded bg-background px-1.5 py-0.5">{serverName}</span> : null}
            {toolName ? <span className="rounded bg-background px-1.5 py-0.5">{toolName}</span> : null}
            <span className="rounded bg-background px-1.5 py-0.5">{entry.key.split('__')[1]}</span>
          </div>
        </div>
      </div>

      {tools.length > 0 ? (
        <div className="grid gap-1">
          {tools.slice(0, 6).map((tool, index) => {
            const record = tool && typeof tool === 'object' ? (tool as Record<string, unknown>) : null
            const name = getRecordString(record, ['name', 'title', 'id']) ?? `Tool ${index + 1}`
            const description = getRecordString(record, ['description'])

            return (
              <div className="rounded-md border border-border/60 bg-background/70 p-2" key={name}>
                <div className="text-xs font-medium">{name}</div>
                {description ? (
                  <div className="line-clamp-2 text-[11px] text-muted-foreground">
                    {description}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : null}

      <ToolPayloadSummary result={result} />
    </Card>
  )
}

function getSkillRuntimeTitle(entry: ToolActivityEntry, result: Record<string, unknown> | null) {
  const request = parseRecordResult(entry.request)
  const skill = getNestedRecord(result, 'skill')
  const skillName =
    getRecordString(skill, ['displayName', 'display_name', 'name', 'id']) ??
    getRecordString(request, ['skillName', 'skill_name', 'query', 'skillId', 'skill_id'])

  if (entry.key.endsWith('__searchSkills')) {
    return skillName ? `Skill search: ${skillName}` : 'Skill search'
  }

  if (entry.key.endsWith('__installSkill')) {
    return skillName ? `Install skill: ${skillName}` : 'Install skill'
  }

  if (entry.key.endsWith('__createSkill')) {
    return skillName ? `Created skill: ${skillName}` : 'Created skill'
  }

  if (entry.key.endsWith('__updateSkill')) {
    return skillName ? `Updated skill: ${skillName}` : 'Updated skill'
  }

  if (entry.key.endsWith('__enableSkill')) {
    return skillName ? `Enabled skill: ${skillName}` : 'Enabled skill'
  }

  if (entry.key.endsWith('__runSkill') || entry.key.endsWith('__getSkillInstructions')) {
    return skillName ? `Loaded skill: ${skillName}` : 'Loaded skill'
  }

  return 'Skill result'
}

function SkillRuntimeResultCard({ entry }: { entry: ToolActivityEntry }) {
  const result = parseRecordResult(entry.result)

  if (!result) return null

  const skill = getNestedRecord(result, 'skill')
  const sourceUrl = getRecordString(result, ['sourceUrl', 'source_url'])
  const instructions = getRecordString(result, ['instructions'])
  const success = result.success !== false && typeof result.error !== 'string'
  const setup = getSkillSetupCard(result)

  return (
    <Card className="space-y-3 border border-border/60 bg-muted/40 p-3">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background">
          <PlayCircle className="size-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate text-sm font-semibold">
              {getSkillRuntimeTitle(entry, result)}
            </div>
            <Badge
              className={success ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : ''}
              variant="outline"
            >
              {success ? 'Complete' : 'Failed'}
            </Badge>
          </div>
          <div className="mt-1 flex flex-wrap gap-1 text-[10px] text-muted-foreground">
            {getRecordString(skill, ['id']) ? (
              <span className="rounded bg-background px-1.5 py-0.5">
                {getRecordString(skill, ['id'])}
              </span>
            ) : null}
            {sourceUrl ? <span className="rounded bg-background px-1.5 py-0.5">{sourceUrl}</span> : null}
            <span className="rounded bg-background px-1.5 py-0.5">{entry.key.split('__')[1]}</span>
          </div>
        </div>
      </div>

      {setup ? (
        <SkillSetupCardView compact setup={setup} />
      ) : instructions ? (
        <div className="rounded-md border border-border/60 bg-background/70 p-2">
          <div className="mb-1 text-xs font-medium">Loaded instructions</div>
          <div className="line-clamp-4 whitespace-pre-wrap text-xs text-muted-foreground">
            {instructions}
          </div>
        </div>
      ) : null}

      {setup ? null : <ToolPayloadSummary result={result} />}
    </Card>
  )
}

function isSkillInstallToolKey(key: string) {
  return (
    key === 'SkillIntegrationTool__installSkill' ||
    key === 'SkillIntegrationTool__createSkill' ||
    key === 'SkillIntegrationTool__enableSkill' ||
    key === 'SkillIntegrationTool__updateSkill' ||
    key === 'SkillsIntegrationTool__installSkill' ||
    key === 'SkillIntegrationTool__addSkill' ||
    key === 'SkillsIntegrationTool__addSkill'
  )
}

export function isMediaGenerationToolKey(key: string): boolean {
  return (
    key.includes('unifiedImage') ||
    key.includes('imageGeneration') ||
    key.includes('imageEdit') ||
    key.includes('flux1Pro') ||
    key.includes('gptImageFull') ||
    key.includes('imageGenerator') ||
    key.includes('stableDiffusion') ||
    key.includes('nanoBanana') ||
    key.includes('videoGenerator') ||
    key.includes('studioVideoGenerator') ||
    key.includes('videoGeneration') ||
    key.includes('modifyVideo') ||
    key.includes('manimAnimation') ||
    key.includes('renderManimAnimation')
  )
}

export function shouldDefaultExpandToolResult(entry: ToolActivityEntry) {
  const result = parseRecordResult(entry.result)

  if (result && isSandboxToolEntry(entry, result)) {
    return true
  }

  if (entry.key.startsWith('liveScores__')) {
    return true
  }

  if (entry.key === 'GeneratedIntegrationTool__draftIntegration') {
    return true
  }

  if (entry.key.startsWith('ContentEngine__')) {
    return true
  }

  if (entry.key.startsWith('VirtualFilesystem__losi__fs_')) {
    return true
  }

  if (entry.key === 'MCPIntegrationTool__installMcpServer') {
    return true
  }

  if (entry.key.startsWith('MCPIntegrationTool__') && getMcpSetupCard(result ?? entry.result)) {
    return true
  }

  if (isSkillInstallToolKey(entry.key)) {
    return true
  }

  if (
    entry.key.startsWith('SkillIntegrationTool__') &&
    getSkillSetupCard(result ?? entry.result)
  ) {
    return true
  }

  if (
    entry.key === 'webScraper__googleSearch' ||
    entry.key.includes('googleSearch') ||
    entry.key === 'webScraper__FetchDataFromUrl' ||
    entry.key === 'webScraper__getWeather'
  ) {
    return true
  }

  // Image/video generation — keep the preview open so Complete ≠ blank card.
  if (isMediaGenerationToolKey(entry.key)) {
    return true
  }

  return false
}

function RenameChatResultCard({ result }: { result: unknown }) {
  const data = parseRecordResult(result)

  if (!data) {
    return null
  }

  const success = data.success !== false
  const title = typeof data.title === 'string' ? data.title : null
  const previousTitle = typeof data.previousTitle === 'string' ? data.previousTitle : null
  const error = typeof data.error === 'string' ? data.error : null
  const isGroupChat = data.isGroupChat === true

  return (
    <Card className="flex flex-col gap-2 border border-border/60 bg-muted/40 p-2">
      <div className="flex items-center gap-2">
        {success ? (
          <CheckCircle2 className="size-4 text-emerald-600" />
        ) : (
          <AlertCircle className="size-4 text-destructive" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium">{success ? 'Chat renamed' : 'Rename failed'}</div>
          {success && title ? (
            <div className="truncate text-xs text-muted-foreground">
              {previousTitle && previousTitle !== title ? `${previousTitle} -> ${title}` : title}
            </div>
          ) : null}
          {!success && error ? <div className="text-xs text-muted-foreground">{error}</div> : null}
        </div>
        <Badge variant="outline" className="shrink-0 text-[10px]">
          {isGroupChat ? 'Group' : 'Chat'}
        </Badge>
      </div>
    </Card>
  )
}

// --------------

function LosiStepsResultCard({ result }: { result: unknown }) {
  const data = parseRecordResult(result)
  const todos = Array.isArray(data?.todos) ? data.todos : []

  if (todos.length === 0) {
    return null
  }

  const doneCount = todos.filter((todo) => {
    const record = todo && typeof todo === 'object' ? (todo as Record<string, unknown>) : null

    return record?.status === 'done'
  }).length

  return (
    <Card className="border border-border/60 bg-muted/40 p-2">
      <div className="mb-2 flex items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2 font-semibold">
          <CheckSquare className="size-3.5 text-muted-foreground" />
          <span>Losi steps updated</span>
        </div>
        <Badge variant="outline" className="border-border/60 text-[10px]">
          {doneCount}/{todos.length}
        </Badge>
      </div>
      <ul className="space-y-1.5">
        {todos.slice(0, 6).map((todo, index) => {
          const record = todo && typeof todo === 'object' ? (todo as Record<string, unknown>) : null
          const label = typeof record?.label === 'string' ? record.label : `Step ${index + 1}`
          const status = typeof record?.status === 'string' ? record.status : 'pending'

          return (
            <li
              key={typeof record?.id === 'string' ? record.id : `step-${index}`}
              className="flex items-center gap-2 text-xs"
            >
              <span
                className={cn(
                  'inline-flex size-4 shrink-0 items-center justify-center rounded-full border text-[10px]',
                  status === 'done'
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : status === 'in_progress'
                      ? 'border-amber-400 bg-amber-50 text-amber-700'
                      : 'border-border bg-background text-muted-foreground'
                )}
              >
                {status === 'done' ? '✓' : index + 1}
              </span>
              <span className="min-w-0 flex-1 truncate">{label}</span>
              <span className="shrink-0 text-[10px] text-muted-foreground">
                {status.replace(/_/g, ' ')}
              </span>
            </li>
          )
        })}
      </ul>
      {todos.length > 6 ? (
        <div className="mt-2 text-[10px] text-muted-foreground">{todos.length - 6} more steps</div>
      ) : null}
    </Card>
  )
}

// --------------

function getStringValue(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function getOptionalStringValue(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function getNumberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function getBooleanValue(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null
}

function getNestedRecord(
  record: Record<string, unknown> | null,
  key: string
): Record<string, unknown> | null {
  const value = record?.[key]
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function getSandboxParams(entry: ToolActivityEntry): Record<string, unknown> | null {
  const request = parseRecordResult(entry.request)
  if (!request) return null

  return (
    getNestedRecord(request, 'parameters') ??
    getNestedRecord(request, 'input') ??
    getNestedRecord(request, 'args') ??
    request
  )
}

function getArrayRecords(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return []

  return value.filter(
    (item): item is Record<string, unknown> =>
      Boolean(item) && typeof item === 'object' && !Array.isArray(item)
  )
}

function getStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []

  const seen = new Set<string>()

  return value
    .filter((item): item is string => typeof item === 'string' && item.length > 0)
    .filter((item) => {
      if (seen.has(item)) return false
      seen.add(item)
      return true
    })
}

const WARNING_DIAGNOSTIC_PATTERN = /\b(warn(?:ing)?|deprecated|deprecation)\b/i
const ERROR_DIAGNOSTIC_PATTERN =
  /\b(error|failed|failure|fatal|exception|traceback|uncaught)\b/i

function isWarningOnlyDiagnosticText(value: string | null | undefined) {
  const text = value?.trim()
  if (!text) return false

  return WARNING_DIAGNOSTIC_PATTERN.test(text) && !ERROR_DIAGNOSTIC_PATTERN.test(text)
}

function colorizeTerminalDiagnostics(value: string) {
  const color = isWarningOnlyDiagnosticText(value) ? '33' : '31'

  return `\x1b[${color}m${value}\x1b[0m`
}

function isSandboxToolEntry(entry: ToolActivityEntry, result: Record<string, unknown> | null) {
  if (/sandbox|codeInterpreter|codeExecutionTool/i.test(entry.key)) return true

  return Boolean(
    result &&
      ('stdout' in result ||
        'stderr' in result ||
        'exitCode' in result ||
        'runtime' in result ||
        'previewUrl' in result ||
        'sandbox' in result ||
        'browser' in result)
  )
}

function getSandboxState(entry: ToolActivityEntry, result: Record<string, unknown>): SandboxState {
  if (entry.status === 'running' || entry.status === 'progress') return 'running'
  if (entry.status === 'error') return 'error'
  const stderr = getStringValue(result.stderr)
  if (
    result.success === false ||
    result.error ||
    (stderr && !isWarningOnlyDiagnosticText(stderr))
  ) {
    return 'error'
  }
  return 'completed'
}

function getSandboxTitle(
  operation: string | null,
  language: string,
  sandboxName: string | null
) {
  if (operation === 'preview') return 'Computer preview'
  if (operation === 'browser') return 'Canvas browser'
  if (operation === 'workspace') return 'Persistent computer'
  if (operation === 'command') return 'Computer command'
  if (operation === 'files') return 'Computer files'
  if (sandboxName) return `Computer · ${sandboxName}`
  return language ? `Computer · ${language}` : 'Code runner'
}

// --------------

function InteractivePreviewFrame({
  url,
  title,
  displayUrl
}: {
  url: string
  title: string
  displayUrl?: string | null
}) {
  return (
    <div
      className="ph-no-capture border-border/70 bg-background min-w-0 overflow-hidden rounded-md border"
      {...{ [COMPUTER_INTERACTION_ATTRIBUTE]: 'true' }}
      onFocusCapture={markComputerInteraction}
      onPointerDownCapture={markComputerInteraction}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-2 py-1.5">
        <div className="min-w-0">
          <div className="text-xs font-medium">{title}</div>
          <div className="truncate text-[10px] text-muted-foreground">{displayUrl ?? url}</div>
        </div>
        <Badge
          variant="outline"
          className="shrink-0 border-emerald-500/40 text-[10px] text-emerald-600 dark:text-emerald-400"
        >
          Takeover
        </Badge>
      </div>
      <iframe
        src={url}
        title={title}
        {...{ [COMPUTER_INTERACTION_ATTRIBUTE]: 'true' }}
        className="ph-no-capture bg-background aspect-video max-h-[420px] min-h-[320px] w-full border-0"
        sandbox="allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts allow-downloads"
        allow="clipboard-read; clipboard-write; fullscreen; autoplay"
        referrerPolicy="no-referrer"
        onFocus={markComputerInteraction}
        onPointerDown={markComputerInteraction}
      />
    </div>
  )
}

// --------------

function BrowserStreamUnavailablePanel({
  error,
  dashboardUrl
}: {
  error: string | null
  dashboardUrl: string | null
}) {
  if (!error && !dashboardUrl) return null

  return (
    <BrowserDetailPanel
      icon={<AlertCircle className="size-3.5 text-amber-600" />}
      title="Live stream"
      detail={
        dashboardUrl
          ? 'Dashboard ready'
          : error
            ? 'Live stream unavailable'
            : 'Dashboard ready'
      }
    >
      {error ? (
        <p className="text-[11px] leading-relaxed text-muted-foreground">{error}</p>
      ) : null}
      {dashboardUrl ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="mt-2 h-8 gap-1.5"
          onClick={() => openInNewTab(dashboardUrl)}
        >
          Open live dashboard <ExternalLink className="size-3.5" />
        </Button>
      ) : null}
    </BrowserDetailPanel>
  )
}

// --------------

function getBrowserActionLabel(action: string | null) {
  switch (action) {
    case 'open':
      return 'Open page'
    case 'snapshot':
      return 'Read page'
    case 'screenshot':
      return 'Capture screenshot'
    case 'click':
      return 'Click'
    case 'dblclick':
      return 'Double click'
    case 'fill':
      return 'Fill input'
    case 'type':
      return 'Type'
    case 'keyboardType':
      return 'Keyboard input'
    case 'press':
      return 'Press key'
    case 'hover':
      return 'Hover'
    case 'focus':
      return 'Focus'
    case 'scroll':
      return 'Scroll'
    case 'wait':
      return 'Wait'
    case 'getText':
      return 'Read text'
    case 'getHtml':
      return 'Read HTML'
    case 'getValue':
      return 'Read value'
    case 'getTitle':
      return 'Read title'
    case 'getUrl':
      return 'Read URL'
    case 'eval':
      return 'Run script'
    case 'back':
      return 'Back'
    case 'forward':
      return 'Forward'
    case 'reload':
      return 'Reload'
    case 'streamStatus':
      return 'Live stream'
    case 'recordStart':
      return 'Start recording'
    case 'recordStop':
      return 'Stop recording'
    case 'recordRestart':
      return 'Restart recording'
    case 'profilerStart':
      return 'Start profile'
    case 'profilerStop':
      return 'Save profile'
    case 'traceStart':
      return 'Start trace'
    case 'traceStop':
      return 'Save trace'
    case 'console':
      return 'Console'
    case 'errors':
      return 'Page errors'
    case 'vitals':
      return 'Web vitals'
    case 'reactTree':
      return 'React tree'
    case 'clipboardRead':
      return 'Read clipboard'
    case 'clipboardWrite':
      return 'Write clipboard'
    case 'pdf':
      return 'Export PDF'
    case 'upload':
      return 'Upload files'
    case 'download':
      return 'Download file'
    case 'waitDownload':
      return 'Wait for download'
    case 'recording':
      return 'Recording'
    case 'profile':
      return 'Performance profile'
    case 'trace':
      return 'Trace'
    case 'close':
      return 'Close browser'
    default:
      return action ?? 'Browser run'
  }
}

// --------------

function stringifyBrowserData(value: unknown) {
  if (value == null) return null
  if (typeof value === 'string') return value

  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

// --------------

function getBrowserPathSummary(record: Record<string, unknown> | null) {
  if (!record) return null

  const path = getStringValue(record.path)
  if (path) return path

  const paths = Array.isArray(record.paths)
    ? record.paths.filter((item): item is string => typeof item === 'string')
    : []

  return paths.length > 0 ? paths.join(', ') : null
}

// --------------

function getBrowserVideoDataUrl(record: Record<string, unknown> | null) {
  const dataUrl = getStringValue(record?.dataUrl)
  if (dataUrl?.startsWith('data:video/')) return dataUrl

  const base64 = getStringValue(record?.base64)
  return base64 ? `data:video/webm;base64,${base64}` : null
}

// --------------

function getBrowserImageDataUrl(record: Record<string, unknown> | null) {
  const dataUrl = getStringValue(record?.dataUrl)
  if (dataUrl?.startsWith('data:image/')) return dataUrl

  const base64 = getStringValue(record?.base64)
  if (!base64) return null

  const mimeType = getStringValue(record?.mimeType) ?? 'image/png'
  return `data:${mimeType};base64,${base64}`
}

// --------------

function getBrowserArtifactUrls(
  record: Record<string, unknown> | null,
  sandboxName: string | null
) {
  const previewUrl =
    getStringValue(record?.previewUrl) ??
    getStringValue(record?.url) ??
    getStringValue(record?.href)
  const downloadUrl =
    getStringValue(record?.downloadUrl) ??
    getStringValue(record?.download_url)
  const path = getStringValue(record?.path)
  const routeLinks =
    !previewUrl && !downloadUrl && path
      ? getSandboxFileLinks(sandboxName, path)
      : null

  return {
    previewUrl: previewUrl ?? routeLinks?.previewUrl ?? null,
    downloadUrl: downloadUrl ?? routeLinks?.downloadUrl ?? null
  }
}

// --------------

function BrowserArtifactActions({
  record,
  sandboxName
}: {
  record: Record<string, unknown> | null
  sandboxName: string | null
}) {
  const { previewUrl, downloadUrl } = getBrowserArtifactUrls(record, sandboxName)
  const publishLevel = getStringValue(record?.publishLevel)

  if (!previewUrl && !downloadUrl && !publishLevel) return null

  return (
    <div className="flex flex-wrap gap-1.5">
      {publishLevel ? (
        <Badge variant="outline" className="h-7 text-[10px]">
          {publishLevel}
        </Badge>
      ) : null}
      {previewUrl ? (
        <Button asChild variant="secondary" size="sm" className="h-7 gap-1.5">
          <a href={previewUrl} target="_blank" rel="noreferrer">
            Open <ExternalLink className="size-3" />
          </a>
        </Button>
      ) : null}
      {downloadUrl ? (
        <Button asChild variant="outline" size="sm" className="h-7 gap-1.5">
          <a href={downloadUrl}>
            Download <Download className="size-3" />
          </a>
        </Button>
      ) : null}
    </div>
  )
}

// --------------

function BrowserPdfArtifactPreview({
  artifact,
  sandboxName
}: {
  artifact: Record<string, unknown> | null
  sandboxName: string | null
}) {
  const path = getStringValue(artifact?.path)
  const mimeType = getStringValue(artifact?.mimeType)
  const kind = getStringValue(artifact?.kind)
  const format = getStringValue(artifact?.format)
  const { previewUrl } = getBrowserArtifactUrls(artifact, sandboxName)
  const shouldPreview =
    Boolean(previewUrl) &&
    (kind === 'pdf' || format === 'pdf' || Boolean(path && isPdfSandboxFile(path, mimeType)))

  if (!previewUrl || !shouldPreview) return null

  return (
    <div className="overflow-hidden rounded-md border border-border/70 bg-background">
      <iframe
        src={previewUrl}
        title="PDF preview"
        className="h-[420px] w-full border-0 bg-background"
      />
    </div>
  )
}

// --------------

function BrowserReplayVideo({
  recording,
  artifact
}: {
  recording: Record<string, unknown> | null
  artifact: Record<string, unknown> | null
}) {
  const src = getBrowserVideoDataUrl(recording) ?? getBrowserVideoDataUrl(artifact)
  if (!src) return null

  return (
    <div className="overflow-hidden rounded-md border border-border/70 bg-black">
      <video
        src={src}
        controls
        playsInline
        className="aspect-video max-h-[360px] w-full bg-black object-contain"
      />
    </div>
  )
}

// --------------

function BrowserImageArtifactPreview({
  artifact
}: {
  artifact: Record<string, unknown> | null
}) {
  const src = getBrowserImageDataUrl(artifact)
  if (!src) return null

  return (
    <div className="overflow-hidden rounded-md border border-border/70 bg-black">
      <Image
        src={src}
        alt="Captured browser artifact"
        width={1280}
        height={720}
        unoptimized
        className="aspect-video max-h-[420px] w-full object-contain"
      />
    </div>
  )
}

// --------------

function BrowserDetailPanel({
  icon,
  title,
  detail,
  children
}: {
  icon: React.ReactNode
  title: string
  detail?: string | null
  children?: React.ReactNode
}) {
  return (
    <div className="min-w-0 rounded-md border border-border/70 bg-background p-2">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 text-muted-foreground">{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium">{title}</div>
          {detail ? (
            <div className="mt-0.5 break-all font-mono text-[10px] text-muted-foreground">
              {detail}
            </div>
          ) : null}
        </div>
      </div>
      {children ? <div className="mt-2 min-w-0">{children}</div> : null}
    </div>
  )
}

// --------------

function BrowserRunStrip({
  action,
  provider,
  device,
  streamUrl,
  dashboardUrl,
  state
}: {
  action: string | null
  provider: string | null
  device: string | null
  streamUrl: string | null
  dashboardUrl: string | null
  state: SandboxState
}) {
  const providerLabel = provider === 'ios' ? 'iOS Safari' : 'Chromium'
  const streamTitle = streamUrl
    ? 'Live viewport'
    : dashboardUrl
      ? 'Live dashboard'
      : state === 'running'
        ? 'Preparing stream'
        : 'Stream'
  const streamDetail = streamUrl
    ? 'Connected surface available'
    : dashboardUrl
      ? 'Handoff surface available'
      : 'No live endpoint yet'

  return (
    <div className="grid gap-2 text-xs sm:grid-cols-3">
      <BrowserDetailPanel
        icon={<MonitorDot className="size-3.5" />}
        title={getBrowserActionLabel(action)}
        detail={providerLabel}
      />
      <BrowserDetailPanel
        icon={<Radio className="size-3.5" />}
        title={streamTitle}
        detail={streamDetail}
      />
      <BrowserDetailPanel
        icon={<Smartphone className="size-3.5" />}
        title={provider === 'ios' ? 'Mobile Safari' : 'Device'}
        detail={device ?? (provider === 'ios' ? 'Waiting for runner' : 'Desktop viewport')}
      />
    </div>
  )
}

// --------------

function BrowserArtifactsPanel({
  recording,
  profile,
  trace,
  artifact,
  transfer,
  clipboard,
  sandboxName
}: {
  recording: Record<string, unknown> | null
  profile: Record<string, unknown> | null
  trace: Record<string, unknown> | null
  artifact: Record<string, unknown> | null
  transfer: Record<string, unknown> | null
  clipboard: Record<string, unknown> | null
  sandboxName: string | null
}) {
  const clipboardText = getStringValue(clipboard?.text)

  return (
    <div className="space-y-2">
      <BrowserPdfArtifactPreview artifact={artifact} sandboxName={sandboxName} />
      <BrowserReplayVideo recording={recording} artifact={artifact} />
      <BrowserImageArtifactPreview artifact={artifact} />
      <div className="grid gap-2 sm:grid-cols-2">
        {recording ? (
          <BrowserDetailPanel
            icon={<Video className="size-3.5" />}
            title="Recording"
            detail={
              getBrowserPathSummary(recording) ??
              getStringValue(recording.state) ??
              getStringValue(recording.output)
            }
          >
            <BrowserArtifactActions record={recording} sandboxName={sandboxName} />
          </BrowserDetailPanel>
        ) : null}
        {profile ? (
          <BrowserDetailPanel
            icon={<Gauge className="size-3.5" />}
            title="Performance profile"
            detail={getBrowserPathSummary(profile) ?? getStringValue(profile.state)}
          >
            <BrowserArtifactActions record={profile} sandboxName={sandboxName} />
          </BrowserDetailPanel>
        ) : null}
        {trace ? (
          <BrowserDetailPanel
            icon={<Bug className="size-3.5" />}
            title="Trace"
            detail={getBrowserPathSummary(trace) ?? getStringValue(trace.state)}
          >
            <BrowserArtifactActions record={trace} sandboxName={sandboxName} />
          </BrowserDetailPanel>
        ) : null}
        {artifact ? (
          <BrowserDetailPanel
            icon={<FileText className="size-3.5" />}
            title={getBrowserActionLabel(getStringValue(artifact.kind))}
            detail={getBrowserPathSummary(artifact) ?? getStringValue(artifact.format)}
          >
            <BrowserArtifactActions record={artifact} sandboxName={sandboxName} />
          </BrowserDetailPanel>
        ) : null}
        {transfer ? (
          <BrowserDetailPanel
            icon={
              getStringValue(transfer.action) === 'upload' ? (
                <Upload className="size-3.5" />
              ) : (
                <Download className="size-3.5" />
              )
            }
            title={getBrowserActionLabel(getStringValue(transfer.action))}
            detail={getBrowserPathSummary(transfer) ?? getStringValue(transfer.selector)}
          >
            <BrowserArtifactActions record={transfer} sandboxName={sandboxName} />
          </BrowserDetailPanel>
        ) : null}
        {clipboard ? (
          <BrowserDetailPanel
            icon={<Clipboard className="size-3.5" />}
            title={getBrowserActionLabel(getStringValue(clipboard.action))}
            detail={clipboardText}
          />
        ) : null}
      </div>
    </div>
  )
}

// --------------

function BrowserDiagnosticsPanel({
  diagnostics,
  metrics,
  reactTree
}: {
  diagnostics: Record<string, unknown> | null
  metrics: Record<string, unknown> | null
  reactTree: Record<string, unknown> | null
}) {
  const diagnosticsOutput =
    stringifyBrowserData(diagnostics?.data) ?? getStringValue(diagnostics?.text)
  const metricsOutput = stringifyBrowserData(metrics?.data) ?? getStringValue(metrics?.text)
  const reactOutput = getStringValue(reactTree?.tree)

  return (
    <div className="space-y-2">
      {diagnostics ? (
        <BrowserDetailPanel
          icon={<Bug className="size-3.5" />}
          title={getBrowserActionLabel(getStringValue(diagnostics.kind))}
          detail={diagnostics.cleared === true ? 'Cleared after read' : null}
        >
          {diagnosticsOutput ? (
            <CodeBlock code={diagnosticsOutput} language="json" />
          ) : null}
        </BrowserDetailPanel>
      ) : null}
      {metrics ? (
        <BrowserDetailPanel
          icon={<Gauge className="size-3.5" />}
          title="Web vitals"
        >
          {metricsOutput ? <CodeBlock code={metricsOutput} language="json" /> : null}
        </BrowserDetailPanel>
      ) : null}
      {reactTree ? (
        <BrowserDetailPanel
          icon={<MonitorDot className="size-3.5" />}
          title="React tree"
        >
          {reactOutput ? <CodeBlock code={reactOutput} language="text" /> : null}
        </BrowserDetailPanel>
      ) : null}
    </div>
  )
}

// --------------

function BrowserMobilePanel({
  provider,
  device,
  hostRequirement,
  handoffReason,
  fallback,
  state
}: {
  provider: string | null
  device: string | null
  hostRequirement: string | null
  handoffReason?: string | null
  fallback?: string | null
  state: SandboxState
}) {
  if (provider !== 'ios' && !hostRequirement) return null
  const requiresMacRunner = hostRequirement === 'macos-xcode-appium'

  return (
    <BrowserDetailPanel
      icon={<Smartphone className="size-3.5" />}
      title={requiresMacRunner ? 'iOS handoff required' : 'Mobile Safari'}
      detail={
        device ??
        (requiresMacRunner
          ? 'Mac runner with Xcode, Appium, and XCUITest'
          : hostRequirement ?? (state === 'running' ? 'Starting' : 'Ready'))
      }
    >
      {handoffReason ? (
        <p className="text-[11px] leading-relaxed text-muted-foreground">{handoffReason}</p>
      ) : null}
      {fallback ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Badge variant="outline" className="text-[10px]">
            Chromium mobile emulation available here
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            Real Safari needs handoff
          </Badge>
        </div>
      ) : null}
    </BrowserDetailPanel>
  )
}

// --------------

function normalizeBrowserHandoffKindKey(kind: string | null) {
  return (kind || '').toLowerCase().replace(/[\s_-]/g, '')
}

function getBrowserHandoffKindLabel(kind: string | null) {
  switch (normalizeBrowserHandoffKindKey(kind)) {
    case 'authentication':
    case 'auth':
      return 'Authentication'
    case 'login':
    case 'signin':
      return 'Login'
    case 'captcha':
    case 'recaptcha':
    case 'hcaptcha':
    case 'turnstile':
      return 'CAPTCHA'
    case 'botchallenge':
    case 'bot':
    case 'challenge':
      return 'Bot check'
    case 'sensitiveaction':
    case 'approval':
    case 'sensitive':
      return 'Approval needed'
    case 'cookie':
    case 'cookies':
    case 'cookieconsent':
    case 'consent':
      return 'Cookie consent'
    default:
      return 'Needs you'
  }
}

function getBrowserHandoffKindBlurb(kind: string | null) {
  switch (normalizeBrowserHandoffKindKey(kind)) {
    case 'captcha':
    case 'recaptcha':
    case 'hcaptcha':
    case 'turnstile':
      return 'Solve the challenge, then the agent can continue.'
    case 'botchallenge':
    case 'bot':
    case 'challenge':
      return 'Complete the verification, then the agent can continue.'
    case 'sensitiveaction':
    case 'approval':
    case 'sensitive':
      return 'Confirm this sensitive step yourself — the agent will wait.'
    case 'cookie':
    case 'cookies':
    case 'cookieconsent':
    case 'consent':
      return 'Accept or decline cookies on the site, then the agent can continue.'
    default:
      return 'A protected step needs you before automation continues.'
  }
}

// --------------

function BrowserHandoffPanel({
  required,
  kind,
  reason,
  takeoverUrl,
  state
}: {
  required: boolean
  kind: string | null
  reason: string | null
  takeoverUrl: string | null
  state: SandboxState
}) {
  if (!required && !reason) return null

  const label = getBrowserHandoffKindLabel(kind)
  const blurb = getBrowserHandoffKindBlurb(kind)
  const isSensitive =
    label === 'Approval needed' ||
    normalizeBrowserHandoffKindKey(kind).includes('sensitive')

  // Solid card — matches private vault; not liquid glass.
  return (
    <div className="my-2 space-y-3 rounded-2xl border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'rounded-xl p-2.5',
            isSensitive ? 'bg-red-500/15' : 'bg-amber-500/15'
          )}
        >
          {isSensitive ? (
            <ShieldAlert className="size-4 text-red-500" />
          ) : (
            <AlertCircle className="size-4 text-amber-500" />
          )}
        </div>
        <div className="min-w-0 space-y-1">
          <div className="text-sm font-semibold">{label}</div>
          <div className="text-xs text-muted-foreground">
            {reason?.trim() || blurb}
          </div>
          {state === 'running' ? (
            <div className="text-[11px] text-muted-foreground">
              Waiting for takeover
            </div>
          ) : null}
        </div>
      </div>
      {takeoverUrl ? (
        <Button
          type="button"
          className="w-full gap-1.5"
          onClick={() => openInNewTab(takeoverUrl)}
        >
          <Hand className="size-3.5" />
          Take over
          <ExternalLink className="size-3.5" />
        </Button>
      ) : null}
    </div>
  )
}

type BrowserNetworkRouteMode = 'direct' | 'custom'

type BrowserNetworkVerificationStatus =
  | 'verified'
  | 'failed'
  | 'pending'
  | 'not checked'

type BrowserNetworkRoute = {
  mode: BrowserNetworkRouteMode
  host: string | null
  bypassList: string[]
  verificationStatus: BrowserNetworkVerificationStatus
}

const NETWORK_HOST_KEYS = [
  'host',
  'hostname',
  'server',
  'endpoint',
  'address',
  'url',
  'proxyUrl',
  'httpProxy',
  'httpsProxy',
  'socksProxy'
] as const

const NETWORK_BYPASS_KEYS = [
  'bypass',
  'bypassList',
  'noProxy',
  'no_proxy',
  'excludedHosts'
] as const

// --------------

function getSanitizedNetworkRouteHost(value: string | null) {
  const trimmed = value?.trim()
  if (!trimmed) return null

  try {
    const parsed = new URL(trimmed.includes('://') ? trimmed : `http://${trimmed}`)
    return parsed.host || null
  } catch {
    const authority = trimmed
      .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
      .split(/[/?#]/)[0]
      ?.split('@')
      .pop()
      ?.trim()

    return authority && !/\s/.test(authority) ? authority : null
  }
}

// --------------

function getStringListValue(value: unknown) {
  const values = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(/[,\n]/)
      : []

  return values
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean)
}

// --------------

function getNetworkRouteHost(records: Record<string, unknown>[]) {
  for (const record of records) {
    for (const key of NETWORK_HOST_KEYS) {
      const host = getSanitizedNetworkRouteHost(getStringValue(record[key]))
      if (host) return host
    }
  }

  return null
}

// --------------

function getNetworkBypassList(records: Record<string, unknown>[]) {
  const seen = new Set<string>()
  const bypassList: string[] = []

  for (const record of records) {
    for (const key of NETWORK_BYPASS_KEYS) {
      for (const item of getStringListValue(record[key])) {
        const sanitized = getSanitizedNetworkRouteHost(item) ?? item
        if (seen.has(sanitized)) continue

        seen.add(sanitized)
        bypassList.push(sanitized)
      }
    }
  }

  return bypassList.slice(0, 12)
}

// --------------

function getBrowserNetworkRouteMode(
  value: string | null,
  host: string | null
): BrowserNetworkRouteMode {
  const normalized = value?.trim().toLowerCase()
  if (normalized === 'direct' || normalized === 'none' || normalized === 'off') {
    return 'direct'
  }

  if (normalized === 'custom' || normalized === 'proxy' || normalized === 'manual') {
    return 'custom'
  }

  return host ? 'custom' : 'direct'
}

// --------------

function normalizeBrowserNetworkVerificationStatus(
  value: string | null
): BrowserNetworkVerificationStatus | null {
  const normalized = value?.trim().toLowerCase()
  if (!normalized) return null

  if (['verified', 'success', 'ok', 'passed', 'reachable'].includes(normalized)) {
    return 'verified'
  }

  if (['failed', 'failure', 'error', 'blocked', 'unreachable'].includes(normalized)) {
    return 'failed'
  }

  if (['pending', 'running', 'checking', 'started'].includes(normalized)) {
    return 'pending'
  }

  return null
}

// --------------

function getBrowserNetworkVerificationStatus(
  records: Record<string, unknown>[]
): BrowserNetworkVerificationStatus {
  for (const record of records) {
    if (record.verified === true) return 'verified'
    if (record.verified === false) return 'failed'

    const verificationRecord = getNestedRecord(record, 'verification')
    const status =
      normalizeBrowserNetworkVerificationStatus(getStringValue(verificationRecord?.status)) ??
      normalizeBrowserNetworkVerificationStatus(getStringValue(record.verificationStatus)) ??
      normalizeBrowserNetworkVerificationStatus(getStringValue(record.status))

    if (status) return status
  }

  return 'not checked'
}

// --------------

function getBrowserNetworkRoute(
  browserRecord: Record<string, unknown> | null
): BrowserNetworkRoute | null {
  if (!browserRecord || (!('proxy' in browserRecord) && !('network' in browserRecord))) {
    return null
  }

  const proxyRecord = getNestedRecord(browserRecord, 'proxy')
  const networkRecord = getNestedRecord(browserRecord, 'network')
  const routeRecord = networkRecord ? getNestedRecord(networkRecord, 'route') : null
  const networkProxyRecord = networkRecord ? getNestedRecord(networkRecord, 'proxy') : null
  const records = [proxyRecord, networkProxyRecord, routeRecord, networkRecord].filter(
    (item): item is Record<string, unknown> => Boolean(item)
  )
  const stringHost = getSanitizedNetworkRouteHost(
    getStringValue(browserRecord.proxy) ?? getStringValue(browserRecord.network)
  )
  const host = getNetworkRouteHost(records) ?? stringHost
  const mode = getBrowserNetworkRouteMode(
    records.map((record) => getStringValue(record.mode)).find(Boolean) ?? null,
    host
  )

  return {
    mode,
    host: mode === 'custom' ? host : null,
    bypassList: getNetworkBypassList(records),
    verificationStatus: getBrowserNetworkVerificationStatus(records)
  }
}

// --------------

function getBrowserNetworkModeLabel(mode: BrowserNetworkRouteMode) {
  return mode === 'custom' ? 'Custom proxy' : 'Direct'
}

// --------------

function getBrowserNetworkStatusLabel(status: BrowserNetworkVerificationStatus) {
  switch (status) {
    case 'verified':
      return 'Verified'
    case 'failed':
      return 'Failed'
    case 'pending':
      return 'Pending'
    default:
      return 'Not checked'
  }
}

// --------------

function getBrowserNetworkStatusIcon(status: BrowserNetworkVerificationStatus) {
  if (status === 'verified') return <CheckCircle2 className="size-3.5 text-emerald-600" />
  if (status === 'failed') return <AlertCircle className="size-3.5 text-destructive" />
  if (status === 'pending') return <Loader2 className="size-3.5 animate-spin text-primary" />

  return <Radio className="size-3.5 text-muted-foreground" />
}

// --------------

function BrowserNetworkPanel({ route }: { route: BrowserNetworkRoute | null }) {
  if (!route) return null

  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-3">
        <BrowserDetailPanel
          icon={<Radio className="size-3.5" />}
          title="Mode"
          detail={getBrowserNetworkModeLabel(route.mode)}
        />
        <BrowserDetailPanel
          icon={<LinkIcon className="size-3.5" />}
          title="Sanitized host"
          detail={
            route.mode === 'direct'
              ? 'Direct connection'
              : route.host ?? 'Custom host hidden'
          }
        />
        <BrowserDetailPanel
          icon={getBrowserNetworkStatusIcon(route.verificationStatus)}
          title="Verification"
          detail={getBrowserNetworkStatusLabel(route.verificationStatus)}
        />
      </div>
      {route.bypassList.length > 0 ? (
        <BrowserDetailPanel
          icon={<Flag className="size-3.5" />}
          title="Bypass list"
        >
          <div className="flex flex-wrap gap-1.5">
            {route.bypassList.map((item) => (
              <Badge key={item} variant="outline" className="max-w-full truncate text-[10px]">
                {item}
              </Badge>
            ))}
          </div>
        </BrowserDetailPanel>
      ) : null}
    </div>
  )
}

type SandboxFileNode = {
  path: string
  name: string
  type: 'file' | 'folder'
  bytes?: number | null
  language?: string | null
  mimeType?: string | null
  contentPreview?: string | null
  dataUrl?: string | null
  downloadUrl?: string | null
  publishLevel?: string | null
  previewUrl?: string | null
  truncated?: boolean
  children: Map<string, SandboxFileNode>
}

// --------------

function createFileNode(
  path: string,
  name: string,
  type: SandboxFileNode['type']
): SandboxFileNode {
  return {
    path,
    name,
    type,
    children: new Map()
  }
}

// --------------

function buildSandboxFileTree(files: Record<string, unknown>[]) {
  const root = createFileNode('', '', 'folder')

  for (const file of files) {
    const path = getStringValue(file.path)
    if (!path) continue

    const parts = path.split('/').filter(Boolean)
    if (parts.length === 0) continue

    let current = root
    let currentPath = ''

    parts.forEach((part, index) => {
      currentPath = currentPath ? `${currentPath}/${part}` : part
      const isFile = index === parts.length - 1
      const existing = current.children.get(part)
      const node = existing ?? createFileNode(currentPath, part, isFile ? 'file' : 'folder')

      if (isFile) {
        node.type = 'file'
        node.bytes = getNumberValue(file.bytes)
        node.language = getStringValue(file.language)
        node.mimeType = getStringValue(file.mimeType)
        node.contentPreview =
          getOptionalStringValue(file.contentPreview) ?? getOptionalStringValue(file.content)
        node.dataUrl = getStringValue(file.dataUrl)
        node.downloadUrl = getStringValue(file.downloadUrl)
        node.publishLevel = getStringValue(file.publishLevel)
        node.previewUrl = getStringValue(file.previewUrl) ?? getStringValue(file.url)
        node.truncated = file.truncated === true
      }

      current.children.set(part, node)
      current = node
    })
  }

  return root
}

// --------------

function getExpandedFolderPaths(node: SandboxFileNode) {
  const paths = new Set<string>()
  const walk = (item: SandboxFileNode) => {
    if (item.type === 'folder' && item.path) {
      paths.add(item.path)
    }

    for (const child of item.children.values()) {
      walk(child)
    }
  }

  walk(node)
  return paths
}

// --------------

function getSandboxFileMap(node: SandboxFileNode) {
  const fileMap = new Map<string, SandboxFileNode>()

  const walk = (item: SandboxFileNode) => {
    if (item.type === 'file') {
      fileMap.set(item.path, item)
      return
    }

    for (const child of item.children.values()) {
      walk(child)
    }
  }

  walk(node)
  return fileMap
}

// --------------

function getFirstSandboxFilePath(node: SandboxFileNode) {
  const files = [...getSandboxFileMap(node).values()].sort((left, right) =>
    left.path.localeCompare(right.path)
  )

  return files[0]?.path ?? null
}

// --------------

function getSandboxFileImageSrc(file: SandboxFileNode | null) {
  const dataUrl = file?.dataUrl
  if (dataUrl?.startsWith('data:image/')) return dataUrl

  return null
}

// --------------

function getSandboxFileIcon(node: SandboxFileNode) {
  const language = node.language?.toLowerCase() ?? ''
  const mimeType = node.mimeType?.toLowerCase() ?? ''
  const path = node.path.toLowerCase()

  if (mimeType.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/.test(path)) {
    return <FileImage className="size-3.5" />
  }

  if (
    language !== 'text' ||
    /\.(tsx?|jsx?|json|css|html?|mdx?|py|sh|bash|mjs|cjs)$/.test(path)
  ) {
    return <FileCode className="size-3.5" />
  }

  return <FileText className="size-3.5" />
}

// --------------

function SandboxFilePreview({
  file,
  sandboxName
}: {
  file: SandboxFileNode | null
  sandboxName: string | null
}) {
  if (!file) {
    return (
      <div className="flex min-h-48 items-center justify-center rounded-md border border-border/70 bg-background/70 p-4 text-xs text-muted-foreground">
        Select a file to preview it here.
      </div>
    )
  }

  const imageSrc = getSandboxFileImageSrc(file)
  const content = file.contentPreview
  const language = file.language ?? 'text'
  const links = getSandboxFileLinks(sandboxName, file.path)
  const previewUrl = file.previewUrl ?? links?.previewUrl ?? null
  const downloadUrl = file.downloadUrl ?? links?.downloadUrl ?? null
  const pdfPreviewUrl = previewUrl && isPdfSandboxFile(file.path, file.mimeType)
    ? previewUrl
    : null

  return (
    <div className="min-w-0 overflow-hidden rounded-md border border-border/70 bg-background">
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-2 py-1.5">
        <div className="min-w-0">
          <div className="truncate text-xs font-medium">{file.name}</div>
          <div className="truncate font-mono text-[10px] text-muted-foreground">{file.path}</div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {file.bytes != null ? (
            <Badge variant="outline" className="text-[10px]">
              {file.bytes} bytes
            </Badge>
          ) : null}
          {file.publishLevel ? (
            <Badge variant="outline" className="text-[10px]">
              {file.publishLevel}
            </Badge>
          ) : null}
          {previewUrl ? (
            <Button asChild variant="ghost" size="sm" className="h-7 gap-1 px-2 text-[11px]">
              <a href={previewUrl} target="_blank" rel="noreferrer">
                Open <ExternalLink className="size-3" />
              </a>
            </Button>
          ) : null}
          {downloadUrl ? (
            <Button asChild variant="outline" size="sm" className="h-7 gap-1 px-2 text-[11px]">
              <a href={downloadUrl}>
                Download <Download className="size-3" />
              </a>
            </Button>
          ) : null}
        </div>
      </div>
      {pdfPreviewUrl ? (
        <iframe
          src={pdfPreviewUrl}
          title={file.name}
          className="h-[420px] w-full border-0 bg-background"
        />
      ) : imageSrc ? (
        <div className="bg-black">
          <Image
            src={imageSrc}
            alt={file.name}
            width={1280}
            height={720}
            unoptimized
            className="aspect-video max-h-[420px] w-full object-contain"
          />
        </div>
      ) : content != null ? (
        <div>
          <CodeBlock code={content} language={language} showLineNumbers />
          {file.truncated ? (
            <div className="border-t border-border/60 px-2 py-1.5 text-[10px] text-muted-foreground">
              Preview truncated.
            </div>
          ) : null}
        </div>
      ) : (
        <div className="p-4 text-xs text-muted-foreground">
          No inline preview is available for this file.
        </div>
      )}
    </div>
  )
}

// --------------

function renderSandboxFileNode(node: SandboxFileNode): React.ReactNode {
  const children = [...node.children.values()].sort((left, right) => {
    if (left.type !== right.type) {
      return left.type === 'folder' ? -1 : 1
    }

    return left.name.localeCompare(right.name)
  })

  if (node.type === 'file') {
    return (
      <FileTreeFile
        key={node.path}
        path={node.path}
        name={node.name}
        icon={getSandboxFileIcon(node)}
      />
    )
  }

  return (
    <FileTreeFolder key={node.path} path={node.path} name={node.name}>
      {children.map(renderSandboxFileNode)}
    </FileTreeFolder>
  )
}

// --------------

function SandboxFilesTree({
  files,
  onOpenWorkspace,
  sandboxName
}: {
  files: Record<string, unknown>[]
  onOpenWorkspace?: () => void
  sandboxName: string | null
}) {
  const root = useMemo(() => buildSandboxFileTree(files), [files])
  const defaultExpanded = useMemo(() => getExpandedFolderPaths(root), [root])
  const fileMap = useMemo(() => getSandboxFileMap(root), [root])
  const firstFilePath = useMemo(() => getFirstSandboxFilePath(root), [root])
  const [selectedPath, setSelectedPath] = useState<string | null>(firstFilePath)
  const children = [...root.children.values()].sort((left, right) => {
    if (left.type !== right.type) {
      return left.type === 'folder' ? -1 : 1
    }

    return left.name.localeCompare(right.name)
  })
  const selectedFile = selectedPath ? fileMap.get(selectedPath) ?? null : null

  useEffect(() => {
    if (selectedPath && fileMap.has(selectedPath)) return
    setSelectedPath(firstFilePath)
  }, [fileMap, firstFilePath, selectedPath])

  return (
    <div className="space-y-3">
      {onOpenWorkspace ? (
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {files.length} {files.length === 1 ? 'file' : 'files'}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={onOpenWorkspace}
          >
            Open workspace <FolderOpen className="size-3.5" />
          </Button>
        </div>
      ) : null}
      <div className="grid gap-3 md:grid-cols-[minmax(180px,0.8fr)_minmax(0,1.2fr)]">
      <FileTree
        defaultExpanded={defaultExpanded}
        selectedPath={selectedPath ?? undefined}
        onSelect={setSelectedPath}
      >
        {children.map(renderSandboxFileNode)}
      </FileTree>
      <SandboxFilePreview file={selectedFile} sandboxName={sandboxName} />
      </div>
    </div>
  )
}

// --------------

function SandboxResultCard({ entry }: { entry: ToolActivityEntry }) {
  const result = parseRecordResult(entry.result)

  if (!result || !isSandboxToolEntry(entry, result)) {
    return null
  }

  const params = getSandboxParams(entry)
  const operation = getStringValue(result.operation)
  const envRecord = getNestedRecord(result, 'env')
  const sandboxRecord = getNestedRecord(result, 'sandbox')
  const browserRecord = getNestedRecord(result, 'browser')
  const browserProxyRecord = browserRecord ? getNestedRecord(browserRecord, 'proxy') : null
  const commandRecord = getNestedRecord(result, 'command')
  const previewRecord = getNestedRecord(result, 'preview')
  const dockerRecord = getNestedRecord(result, 'docker')
  const agentBrowserRecord = getNestedRecord(result, 'agentBrowser')
  const templateRecord = sandboxRecord
    ? getNestedRecord(sandboxRecord, 'template')
    : null
  const code = getStringValue(params?.code) ?? ''
  const language =
    getStringValue(params?.language) ??
    getStringValue(result.language) ??
    getStringValue(result.runtime) ??
    'log'
  const stdout = getStringValue(result.stdout)
  const stderr = getStringValue(result.stderr)
  const error = getStringValue(result.error)
  const exitCode = typeof result.exitCode === 'number' ? result.exitCode : null
  const hasError = Boolean(
    error || (stderr && !isWarningOnlyDiagnosticText(stderr)) || (exitCode && exitCode !== 0)
  )
  const state = getSandboxState(entry, result)
  const sandboxName = getStringValue(sandboxRecord?.name)
  const sandboxRuntime = getStringValue(sandboxRecord?.runtime)
  const sandboxStatus = getStringValue(sandboxRecord?.status)
  const sandboxSourceSnapshotId = getStringValue(sandboxRecord?.sourceSnapshotId)
  const sandboxTemplate = getStringValue(templateRecord?.name)
  const sandboxTemplateEnvVar = getStringValue(templateRecord?.envVar)
  const sandboxPersistent =
    typeof sandboxRecord?.persistent === 'boolean' ? sandboxRecord.persistent : null
  const previewUrl = getStringValue(result.previewUrl)
  const streamRecord = browserRecord ? getNestedRecord(browserRecord, 'stream') : null
  const dashboardRecord = browserRecord ? getNestedRecord(browserRecord, 'dashboard') : null
  const dashboardEnabled = getBooleanValue(dashboardRecord?.enabled)
  const dashboardError = getStringValue(dashboardRecord?.error)
  const takeoverUrl =
    getStringValue(result.takeoverUrl) ??
    getStringValue(browserRecord?.takeoverUrl) ??
    getStringValue(result.novnc_url)
  const interactivePreviewUrl = takeoverUrl ?? previewUrl
  const interactivePreviewTitle = takeoverUrl ? 'Interactive takeover' : 'Interactive preview'
  const port = getNumberValue(result.port)
  const previewDisplayLabel = getComputerRouteDisplayLabel(
    previewUrl,
    port ? `Preview route :${port}` : 'Preview route'
  )
  const takeoverDisplayLabel = getComputerRouteDisplayLabel(
    takeoverUrl,
    'Takeover surface'
  )
  const dashboardUrl =
    getStringValue(browserRecord?.dashboardUrl) ??
    (dashboardEnabled === false ? null : getStringValue(dashboardRecord?.url)) ??
    getStringValue(streamRecord?.dashboardUrl) ??
    getStringValue(result.dashboardUrl)
  const dashboardDisplayLabel = getComputerRouteDisplayLabel(
    dashboardUrl,
    'Live browser dashboard'
  )
  const streamEnabled = getBooleanValue(streamRecord?.enabled)
  const streamError = getStringValue(streamRecord?.error)
  const streamUrl =
    streamEnabled === false
      ? null
      : getStringValue(browserRecord?.streamWebSocketUrl) ??
        getStringValue(browserRecord?.webSocketUrl) ??
        getStringValue(browserRecord?.streamUrl) ??
        getStringValue(streamRecord?.webSocketUrl) ??
        getStringValue(streamRecord?.url) ??
        getStringValue(result.streamWebSocketUrl) ??
        getStringValue(result.streamUrl)
  const screenshotBase64 =
    getStringValue(browserRecord?.screenshotBase64) ??
    getStringValue(result.screenshotBase64)
  const screenshotSrc = screenshotBase64?.startsWith('data:')
    ? screenshotBase64
    : screenshotBase64
      ? `data:image/png;base64,${screenshotBase64}`
      : null
  const browserProvider = getStringValue(browserRecord?.provider)
  const browserAction = getStringValue(browserRecord?.action)
  const shouldPreferBrowserScreenshot = Boolean(
    screenshotSrc && (browserAction === 'screenshot' || !streamUrl)
  )
  const browserUrl = getStringValue(browserRecord?.url)
  const safeBrowserUrl = redactUrlCredentials(browserUrl)
  const browserProxyUrl =
    getStringValue(browserProxyRecord?.url) ??
    getStringValue(browserProxyRecord?.server) ??
    getStringValue(browserProxyRecord?.proxyUrl) ??
    getStringValue(browserRecord?.proxy)
  const browserProxyLabel =
    browserProxyRecord || (browserRecord && 'proxy' in browserRecord)
      ? redactProxyCredentials(browserProxyUrl) ?? 'proxy configured'
      : null
  const browserDevice =
    getStringValue(browserRecord?.device) ?? getStringValue(browserRecord?.udid)
  const browserHostRequirement = getStringValue(browserRecord?.hostRequirement)
  const browserHandoffRecord = browserRecord
    ? getNestedRecord(browserRecord, 'handoff')
    : null
  const browserHandoffRequired =
    browserRecord?.handoffRequired === true || browserHandoffRecord?.required === true
  const browserHandoffKind =
    getStringValue(browserRecord?.handoffKind) ??
    getStringValue(browserHandoffRecord?.kind)
  const browserHandoffReason =
    getStringValue(browserRecord?.handoffReason) ??
    getStringValue(browserHandoffRecord?.reason)
  const browserFallback = getStringValue(browserRecord?.fallback)
  const recordingRecord = browserRecord ? getNestedRecord(browserRecord, 'recording') : null
  const profileRecord = browserRecord ? getNestedRecord(browserRecord, 'profile') : null
  const traceRecord = browserRecord ? getNestedRecord(browserRecord, 'trace') : null
  const artifactRecord = browserRecord ? getNestedRecord(browserRecord, 'artifact') : null
  const transferRecord = browserRecord ? getNestedRecord(browserRecord, 'transfer') : null
  const clipboardRecord = browserRecord ? getNestedRecord(browserRecord, 'clipboard') : null
  const diagnosticsRecord = browserRecord ? getNestedRecord(browserRecord, 'diagnostics') : null
  const metricsRecord = browserRecord ? getNestedRecord(browserRecord, 'metrics') : null
  const reactRecord = browserRecord ? getNestedRecord(browserRecord, 'react') : null
  const networkRoute = getBrowserNetworkRoute(browserRecord)
  const browserSnapshot =
    getStringValue(browserRecord?.snapshot) ?? getStringValue(result.snapshot)
  const commandLine =
    getStringValue(commandRecord?.line) ??
    getStringValue(params?.command) ??
    getStringValue(params?.cmd)
  const commandId = getStringValue(commandRecord?.id)
  const injectedEnvKeys = getStringArray(envRecord?.injectedKeys)
  const files = getArrayRecords(result.files)
  const sandboxPorts = getArrayRecords(sandboxRecord?.ports)
  const viteAllowedHostConfig = previewRecord?.viteAllowedHostConfig === true
  const hasPreviewSurface = Boolean(
    operation === 'browser' ||
      previewUrl ||
      takeoverUrl ||
      screenshotSrc ||
      streamUrl ||
      dashboardUrl
  )
  const shouldShowOutput = Boolean(
    stdout || stderr || commandLine || operation === 'command' || state === 'running'
  )
  const hasArtifactSurface = Boolean(
    recordingRecord ||
      profileRecord ||
      traceRecord ||
      artifactRecord ||
      transferRecord ||
      clipboardRecord
  )
  const hasDiagnosticsSurface = Boolean(diagnosticsRecord || metricsRecord || reactRecord)
  const hasMobileSurface = Boolean(browserProvider === 'ios' || browserHostRequirement)
  const hasNetworkSurface = Boolean(networkRoute)
  const hasHandoffSurface = Boolean(browserHandoffRequired || browserHandoffReason)
  const terminalOutput = [
    commandLine ? `\x1b[36m$ ${commandLine}\x1b[0m` : '',
    stdout ?? '',
    stderr ? colorizeTerminalDiagnostics(stderr) : '',
    !stdout && !stderr && state === 'running'
      ? getStringValue(result.message) ?? 'Running command...'
      : ''
  ]
    .filter(Boolean)
    .join('\n')
  const defaultTab = hasError
    ? shouldShowOutput
      ? 'output'
      : 'error'
    : hasPreviewSurface
      ? 'preview'
      : browserSnapshot
        ? 'snapshot'
        : code
          ? 'code'
          : hasDiagnosticsSurface
            ? 'diagnostics'
            : hasArtifactSurface
              ? 'artifacts'
              : files.length > 0
                ? 'files'
              : hasHandoffSurface
                ? 'handoff'
                : hasNetworkSurface
                  ? 'network'
                  : shouldShowOutput
                    ? 'output'
                    : 'session'
  const title = getSandboxTitle(operation, language, sandboxName)
  const openWorkspaceInCanvas = () => {
    openMiniIdeWorkspace({
      id: ['computer', entry.key, sandboxName ?? title].filter(Boolean).join(':'),
      title: previewUrl ? 'Computer preview' : 'Computer workspace',
      source: 'computer',
      status: state,
      toolName: entry.key,
      files,
      output: terminalOutput,
      previewUrl
    })
  }

  return (
    <Sandbox className="w-full min-w-0">
      <SandboxHeader
        icon={<IconDeviceLaptop className="text-muted-foreground size-3.5 shrink-0" />}
        state={state}
        title={title}
      />
      <SandboxContent>
        <SandboxTabs defaultValue={defaultTab}>
          <SandboxTabsBar>
            <SandboxTabsList>
              <SandboxTabsTrigger value="session">Session</SandboxTabsTrigger>
              {hasPreviewSurface && (
                <SandboxTabsTrigger value="preview">Preview</SandboxTabsTrigger>
              )}
              {browserSnapshot && (
                <SandboxTabsTrigger value="snapshot">DOM</SandboxTabsTrigger>
              )}
              {code && <SandboxTabsTrigger value="code">Code</SandboxTabsTrigger>}
              {shouldShowOutput && (
                <SandboxTabsTrigger value="output">Output</SandboxTabsTrigger>
              )}
              {files.length > 0 && <SandboxTabsTrigger value="files">Files</SandboxTabsTrigger>}
              {hasArtifactSurface && (
                <SandboxTabsTrigger value="artifacts">Artifacts</SandboxTabsTrigger>
              )}
              {hasDiagnosticsSurface && (
                <SandboxTabsTrigger value="diagnostics">Diagnostics</SandboxTabsTrigger>
              )}
              {hasHandoffSurface && (
                <SandboxTabsTrigger value="handoff">Handoff</SandboxTabsTrigger>
              )}
              {hasNetworkSurface && (
                <SandboxTabsTrigger value="network">Network</SandboxTabsTrigger>
              )}
              {hasMobileSurface && (
                <SandboxTabsTrigger value="mobile">Mobile</SandboxTabsTrigger>
              )}
              {hasError && <SandboxTabsTrigger value="error">Error</SandboxTabsTrigger>}
            </SandboxTabsList>
          </SandboxTabsBar>
          <SandboxTabContent value="session">
            <div className="space-y-3 text-xs">
              <div className="grid gap-2 sm:grid-cols-2">
                {sandboxName && (
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Computer</div>
                    <div className="mt-0.5 break-all font-medium">{sandboxName}</div>
                  </div>
                )}
                {sandboxRuntime && (
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Runtime</div>
                    <div className="mt-0.5 font-medium">{sandboxRuntime}</div>
                  </div>
                )}
                {sandboxStatus && (
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Status</div>
                    <div className="mt-0.5 font-medium">{sandboxStatus}</div>
                  </div>
                )}
                {sandboxPersistent != null && (
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Persistence</div>
                    <div className="mt-0.5 font-medium">
                      {sandboxPersistent ? 'Persistent' : 'Ephemeral'}
                    </div>
                  </div>
                )}
                {sandboxTemplate && (
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Template</div>
                    <div className="mt-0.5 font-medium">{sandboxTemplate}</div>
                  </div>
                )}
                {sandboxSourceSnapshotId && (
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Snapshot</div>
                    <div className="mt-0.5 break-all font-medium">
                      {sandboxSourceSnapshotId}
                    </div>
                  </div>
                )}
                {browserProvider && (
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Browser</div>
                    <div className="mt-0.5 font-medium">
                      {[
                        browserProvider === 'ios' ? 'iOS Safari' : browserProvider,
                        getBrowserActionLabel(browserAction)
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                )}
                {browserProxyLabel && (
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Proxy</div>
                    <div className="mt-0.5 break-all font-medium">{browserProxyLabel}</div>
                  </div>
                )}
                {browserDevice && (
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Device</div>
                    <div className="mt-0.5 break-all font-medium">{browserDevice}</div>
                  </div>
                )}
                {port && (
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Port</div>
                    <div className="mt-0.5 font-medium">{port}</div>
                  </div>
                )}
              </div>
              {operation === 'browser' && (
                <BrowserRunStrip
                  action={browserAction}
                  provider={browserProvider}
                  device={browserDevice}
                  streamUrl={streamUrl}
                  dashboardUrl={dashboardUrl}
                  state={state}
                />
              )}
              {streamError || dashboardError || (!streamUrl && dashboardUrl) ? (
                <BrowserStreamUnavailablePanel
                  error={streamError ?? dashboardError}
                  dashboardUrl={dashboardUrl}
                />
              ) : null}
              {hasMobileSurface && (
                <BrowserMobilePanel
                  provider={browserProvider}
                  device={browserDevice}
                  hostRequirement={browserHostRequirement}
                  handoffReason={browserHandoffReason}
                  fallback={browserFallback}
                  state={state}
                />
              )}
              {hasHandoffSurface && (
                <BrowserHandoffPanel
                  required={browserHandoffRequired}
                  kind={browserHandoffKind}
                  reason={browserHandoffReason}
                  takeoverUrl={takeoverUrl}
                  state={state}
                />
              )}
              {hasNetworkSurface && <BrowserNetworkPanel route={networkRoute} />}
              {injectedEnvKeys.length > 0 && (
                <div className="space-y-1">
                  <div className="text-muted-foreground text-[10px] uppercase">
                    Injected env names
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {injectedEnvKeys.map((key) => (
                      <Badge key={key} variant="outline" className="font-mono text-[10px]">
                        {key}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {commandLine && (
                <div>
                  <div className="mb-1 text-[10px] uppercase text-muted-foreground">Command</div>
                  <CodeBlock code={commandLine} language="bash" />
                </div>
              )}
              {commandId && (
                <Badge variant="outline" className="text-[10px]">
                  Command {commandId}
                </Badge>
              )}
              {dockerRecord && (
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-[10px]">
                    Docker {dockerRecord.ready ? 'ready' : 'not ready'}
                  </Badge>
                  {dockerRecord.daemonStarted ? (
                    <Badge variant="outline" className="text-[10px]">
                      daemon started
                    </Badge>
                  ) : null}
                </div>
              )}
              {agentBrowserRecord && (
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-[10px]">
                    agent-browser {agentBrowserRecord.ready ? 'ready' : 'not ready'}
                  </Badge>
                  {agentBrowserRecord.browserInstalled ? (
                    <Badge variant="outline" className="text-[10px]">
                      browser installed
                    </Badge>
                  ) : null}
                </div>
              )}
              {sandboxTemplateEnvVar && (
                <Badge variant="outline" className="text-[10px]">
                  prebuilt template ready
                </Badge>
              )}
              {viteAllowedHostConfig && (
                <Badge variant="outline" className="text-[10px]">
                  preview host allowed
                </Badge>
              )}
              {sandboxPorts.length > 0 && (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase text-muted-foreground">Routes</div>
                  {sandboxPorts.map((route, index) => {
                    const routePort = getNumberValue(route.port)
                    const routeUrl = getStringValue(route.url)
                    const safeRouteUrl = redactUrlCredentials(routeUrl)
                    return (
                      <div key={`${routePort ?? index}-${safeRouteUrl ?? 'route'}`} className="flex items-center justify-between gap-2 rounded border border-border/60 px-2 py-1">
                        <div className="min-w-0">
                          <span className="font-mono text-[11px]">:{routePort ?? '?'}</span>
                          {safeRouteUrl ? (
                            <span className="ml-2 truncate font-mono text-[10px] text-muted-foreground">
                              {safeRouteUrl}
                            </span>
                          ) : null}
                        </div>
                        {routeUrl ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 gap-1 px-2 text-[11px]"
                            onClick={() => openInNewTab(routeUrl)}
                          >
                            Open <ExternalLink className="size-3" />
                          </Button>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">No route</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </SandboxTabContent>
          {hasPreviewSurface && (
            <SandboxTabContent value="preview">
              <div className="space-y-3">
                {operation === 'browser' && (
                  <BrowserRunStrip
                    action={browserAction}
                    provider={browserProvider}
                    device={browserDevice}
                    streamUrl={streamUrl}
                    dashboardUrl={dashboardUrl}
                    state={state}
                  />
                )}
                <div className="flex flex-wrap gap-2">
                  {previewUrl && (
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 gap-1.5"
                      onClick={() => openInNewTab(previewUrl)}
                    >
                      Open preview <ExternalLink className="size-3.5" />
                    </Button>
                  )}
                  {takeoverUrl && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-8 gap-1.5"
                      onClick={() => openInNewTab(takeoverUrl)}
                    >
                      Take over <ExternalLink className="size-3.5" />
                    </Button>
                  )}
                  {dashboardUrl && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5"
                      onClick={() => openInNewTab(dashboardUrl)}
                    >
                      Open live dashboard <ExternalLink className="size-3.5" />
                    </Button>
                  )}
                  {safeBrowserUrl && (
                    <Badge variant="outline" className="max-w-full truncate text-[10px]">
                      {safeBrowserUrl}
                    </Badge>
                  )}
                  {previewDisplayLabel && previewDisplayLabel !== safeBrowserUrl ? (
                    <Badge variant="outline" className="max-w-full truncate text-[10px]">
                      {previewDisplayLabel}
                    </Badge>
                  ) : null}
                  {takeoverDisplayLabel && takeoverDisplayLabel !== previewDisplayLabel ? (
                    <Badge variant="outline" className="max-w-full truncate text-[10px]">
                      {takeoverDisplayLabel}
                    </Badge>
                  ) : null}
                  {dashboardDisplayLabel && (
                    <Badge variant="outline" className="max-w-full truncate text-[10px]">
                      {dashboardDisplayLabel}
                    </Badge>
                  )}
                </div>
                {shouldPreferBrowserScreenshot && screenshotSrc ? (
                  <div className="aspect-video max-h-[360px] overflow-hidden rounded border border-border/70 bg-black">
                    <Image
                      src={screenshotSrc}
                      alt="Browser screenshot"
                      width={1280}
                      height={720}
                      unoptimized
                      className="size-full object-contain"
                    />
                  </div>
                ) : dashboardUrl ? (
                  <InteractivePreviewFrame
                    url={dashboardUrl}
                    title="Live browser dashboard"
                    displayUrl={dashboardDisplayLabel}
                  />
                ) : hasHandoffSurface ? (
                  <BrowserHandoffPanel
                    required={browserHandoffRequired}
                    kind={browserHandoffKind}
                    reason={browserHandoffReason}
                    takeoverUrl={takeoverUrl}
                    state={state}
                  />
                ) : streamError || dashboardError ? (
                  <BrowserStreamUnavailablePanel
                    error={streamError ?? dashboardError}
                    dashboardUrl={dashboardUrl}
                  />
                ) : interactivePreviewUrl ? (
                  <InteractivePreviewFrame
                    url={interactivePreviewUrl}
                    title={interactivePreviewTitle}
                    displayUrl={takeoverDisplayLabel ?? previewDisplayLabel}
                  />
                ) : browserProvider === 'ios' && browserHostRequirement ? (
                  <BrowserMobilePanel
                    provider={browserProvider}
                    device={browserDevice}
                    hostRequirement={browserHostRequirement}
                    handoffReason={browserHandoffReason}
                    fallback={browserFallback}
                    state={state}
                  />
                ) : screenshotSrc ? (
                  <div className="aspect-video max-h-[360px] overflow-hidden rounded border border-border/70 bg-black">
                    <Image
                      src={screenshotSrc}
                      alt="Browser screenshot"
                      width={1280}
                      height={720}
                      unoptimized
                      className="size-full object-contain"
                    />
                  </div>
                ) : null}
              </div>
            </SandboxTabContent>
          )}
          {browserSnapshot && (
            <SandboxTabContent value="snapshot">
              <CodeBlock code={browserSnapshot} language="text" />
            </SandboxTabContent>
          )}
          {code && (
            <SandboxTabContent value="code">
              <CodeBlock
                code={code}
                language={language === 'python3.13' ? 'python' : language}
                showLineNumbers
              />
            </SandboxTabContent>
          )}
          {shouldShowOutput && (
            <SandboxTabContent value="output">
              <Terminal output={terminalOutput} isStreaming={state === 'running'} />
            </SandboxTabContent>
          )}
          {files.length > 0 && (
            <SandboxTabContent value="files">
              <SandboxFilesTree
                files={files}
                onOpenWorkspace={openWorkspaceInCanvas}
                sandboxName={sandboxName}
              />
            </SandboxTabContent>
          )}
          {hasArtifactSurface && (
            <SandboxTabContent value="artifacts">
              <BrowserArtifactsPanel
                recording={recordingRecord}
                profile={profileRecord}
                trace={traceRecord}
                artifact={artifactRecord}
                transfer={transferRecord}
                clipboard={clipboardRecord}
                sandboxName={sandboxName}
              />
            </SandboxTabContent>
          )}
          {hasDiagnosticsSurface && (
            <SandboxTabContent value="diagnostics">
              <BrowserDiagnosticsPanel
                diagnostics={diagnosticsRecord}
                metrics={metricsRecord}
                reactTree={reactRecord}
              />
            </SandboxTabContent>
          )}
          {hasHandoffSurface && (
            <SandboxTabContent value="handoff">
              <BrowserHandoffPanel
                required={browserHandoffRequired}
                kind={browserHandoffKind}
                reason={browserHandoffReason}
                takeoverUrl={takeoverUrl}
                state={state}
              />
            </SandboxTabContent>
          )}
          {hasNetworkSurface && (
            <SandboxTabContent value="network">
              <BrowserNetworkPanel route={networkRoute} />
            </SandboxTabContent>
          )}
          {hasMobileSurface && (
            <SandboxTabContent value="mobile">
              <BrowserMobilePanel
                provider={browserProvider}
                device={browserDevice}
                hostRequirement={browserHostRequirement}
                handoffReason={browserHandoffReason}
                fallback={browserFallback}
                state={state}
              />
            </SandboxTabContent>
          )}
          {hasError && (
            <SandboxTabContent value="error">
              <StackTrace
                trace={[error, stderr, exitCode != null ? `Exit code: ${exitCode}` : null]
                  .filter((item): item is string => Boolean(item))
                  .join('\n\n')}
              />
            </SandboxTabContent>
          )}
        </SandboxTabs>
      </SandboxContent>
    </Sandbox>
  )
}

// --------------

function FetchedUrlResultCard({ result }: { result: FetchedUrlNormalizedResult }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const markdownPreview = useMemo(() => {
    if (!result.markdown) return ''
    const max = expanded ? 4000 : 400
    const raw = expanded ? result.markdown : stripMarkdownForPreview(result.markdown)
    return raw.length > max ? `${raw.slice(0, max)}…` : raw
  }, [result.markdown, expanded])

  return (
    <Card className="flex flex-col gap-2 border border-border/60 bg-muted/40 p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <LinkIcon className="size-3 shrink-0" />
            <span className="truncate">{result.url || t('Fetched URL')}</span>
          </div>
          {result.title && <div className="text-xs font-medium leading-snug">{result.title}</div>}
        </div>
        {result.url && (
          <Button
            size="icon"
            variant="ghost"
            className="shrink-0"
            onClick={() => openInNewTab(result.url)}
            aria-label={t('Open in new tab')}
          >
            <ExternalLink className="size-4" />
          </Button>
        )}
      </div>
      {result.markdown && (
        <div className="rounded-md bg-background/60 p-2 text-xs">
          <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words font-mono">
            {markdownPreview}
          </pre>
          {result.markdown.length > 400 && (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="mt-1 text-xs text-primary hover:underline"
            >
              {expanded ? t('Show less') : t('Show more')}
            </button>
          )}
        </div>
      )}
    </Card>
  )
}

// --------------

function WeatherResultCard({ result }: { result: WeatherNormalizedResult }) {
  const { t } = useTranslation()

  const tempText =
    typeof result.temperatureC === 'number'
      ? `${result.temperatureC.toFixed(1)}°C`
      : typeof result.temperatureF === 'number'
        ? `${result.temperatureF.toFixed(1)}°F`
        : null

  return (
    <Card className="flex items-center justify-between gap-3 border border-border/60 bg-sky-500/5 p-2">
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wide text-sky-500">
          {t('Weather')}
        </div>
        <div className="text-xs text-muted-foreground">
          {result.location || t('Unknown location')}
        </div>
        {result.summary && <div className="text-xs font-medium leading-snug">{result.summary}</div>}
      </div>
      {tempText && <div className="text-right text-lg font-semibold text-sky-500">{tempText}</div>}
    </Card>
  )
}

// --------------

function FinanceResultCard({ result }: { result: FinanceQuoteNormalizedResult }) {
  const { t } = useTranslation()

  const changeSign =
    typeof result.change === 'number' && result.change !== 0 ? (result.change > 0 ? '+' : '−') : ''

  const changePercentText =
    typeof result.changePercent === 'number' ? `${result.changePercent.toFixed(2)}%` : null

  const green = typeof result.change === 'number' && result.change > 0
  const red = typeof result.change === 'number' && result.change < 0

  const colorClass = green ? 'text-emerald-500' : red ? 'text-rose-500' : 'text-muted-foreground'

  return (
    <Card className="flex items-center justify-between gap-3 border border-border/60 bg-muted/40 p-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-xs">
          {result.symbol && <span className="font-mono font-semibold">{result.symbol}</span>}
          {result.currency && (
            <Badge variant="outline" className="border-border/60 text-[10px] uppercase">
              {result.currency}
            </Badge>
          )}
        </div>
        {result.name && <div className="text-xs text-muted-foreground">{result.name}</div>}
      </div>
      <div className="text-right">
        {typeof result.price === 'number' && (
          <div className="text-sm font-semibold">
            {result.price.toLocaleString(undefined, {
              maximumFractionDigits: 4
            })}
          </div>
        )}
        <div className={cn('text-xs', colorClass)}>
          {changeSign &&
            typeof result.change === 'number' &&
            `${changeSign}${Math.abs(result.change).toFixed(2)}`}
          {changePercentText && (
            <>
              {' '}
              <span className="opacity-80">({changePercentText})</span>
            </>
          )}
          {!changeSign && !changePercentText && (
            <span className="text-muted-foreground">{t('No change data')}</span>
          )}
        </div>
      </div>
    </Card>
  )
}

// --------------

function VirtualFileResultCard({
  result
}: {
  result: VirtualFileSingleNormalizedResult | VirtualFileListNormalizedResult
}) {
  const { t } = useTranslation()

  const openVirtualFile = (file: { id: string; path: string; name: string; content?: string }) => {
    window.dispatchEvent(
      new CustomEvent('openVirtualFileInCanvas', {
        cancelable: true,
        detail: {
          id: file.id,
          path: file.path,
          name: file.name,
          content: file.content
        }
      })
    )
  }

  const files = result.kind === 'virtual_file_single' ? [result.file] : result.files
  const openWorkspace = () => {
    openMiniIdeWorkspace({
      id: `virtual-files:${files.map((file) => file.id).join(':')}`,
      title: 'Files',
      source: 'virtual-files',
      status: 'completed',
      toolName: result.key,
      files
    })
  }

  return (
    <div className="space-y-1">
      <div className="mb-2 flex items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/30 px-2 py-1.5">
        <div className="text-xs text-muted-foreground">
          {files.length} {files.length === 1 ? t('file') : t('files')}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-[11px]"
          onClick={openWorkspace}
        >
          {t('Open workspace')}
        </Button>
      </div>
      {files.map((file) => {
        const lowerPath = file.path.toLowerCase()
        const lowerName = file.name.toLowerCase()
        const isMarkdown = lowerPath.endsWith('.md') || lowerName.endsWith('.md')
        const canPreview =
          isMarkdown ||
          /\.(html?|svg|csv|tsv|json|png|jpe?g|gif|webp|mdx)$/i.test(lowerPath) ||
          /\.(html?|svg|csv|tsv|json|png|jpe?g|gif|webp|mdx)$/i.test(lowerName)
        const segments = file.path.split('/').filter(Boolean)
        const depth = Math.max(0, segments.length - 1)

        return (
          <button
            key={file.id}
            type="button"
            onClick={() => openVirtualFile(file)}
            className="flex w-full items-center gap-2 rounded-md border border-border/60 bg-muted/30 p-2 text-left text-xs transition-colors hover:bg-muted/50"
            style={{ paddingLeft: `${8 + depth * 12}px` }}
          >
            <FileText className="size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-mono">{file.name}</div>
              {!isMarkdown && (
                <div className="truncate text-[10px] text-muted-foreground">{file.path}</div>
              )}
            </div>
            <span className="shrink-0 text-[11px] font-medium text-primary">
              {canPreview ? t('Preview') : t('Open')}
            </span>
          </button>
        )
      })}
    </div>
  )
}

// --------------

function TaskResultCard({ result }: { result: TaskNormalizedResult }) {
  const { t } = useTranslation()

  const tasks = result.tasks

  return (
    <Card className="border border-border/60 bg-muted/40 p-2">
      <div className="mb-1 flex items-center justify-between gap-2 text-xs">
        <div className="font-semibold">{result.kind === 'task_list' ? t('Tasks') : t('Task')}</div>
        <Badge variant="outline" className="border-border/60 text-[10px] uppercase">
          {tasks.length}
        </Badge>
      </div>
      <ul className="space-y-1">
        {tasks.map((task, index) => (
          <li key={task.id ?? index} className="flex items-center gap-2 text-xs">
            <span className="inline-flex size-4 items-center justify-center rounded border border-border/60 bg-background/60 text-[10px]">
              {index + 1}
            </span>
            <div className="min-w-0">
              <div className="truncate font-medium">{task.title || t('Untitled task')}</div>
              <div className="text-[10px] text-muted-foreground">
                {task.status && <span className="mr-1">{task.status}</span>}
                {task.due_date && (
                  <span>
                    • {t('Due')} {new Date(task.due_date).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  )
}

// --------------

function MemoryResultCard({ result }: { result: MemoryNormalizedResult }) {
  const { t } = useTranslation()

  const actionLabel =
    result.action === 'store'
      ? t('Stored memories')
      : result.action === 'list'
        ? t('Memories')
        : result.action === 'update'
          ? t('Updated memories')
          : result.action === 'delete'
            ? t('Deleted memories')
            : t('Memories')

  return (
    <Card className="border border-border/60 bg-muted/40 p-2">
      <div className="mb-1 flex items-center justify-between gap-2 text-xs">
        <div className="font-semibold">{actionLabel}</div>
        <Badge variant="outline" className="border-border/60 text-[10px] uppercase">
          {result.items?.length ?? 0}
        </Badge>
      </div>
      <ul className="space-y-1">
        {(result.items || []).map((item, index) => (
          <li key={item.id ?? index} className="text-xs">
            <div className="flex items-center gap-1">
              {item.key && (
                <span className="font-mono text-[11px] text-muted-foreground">{item.key}</span>
              )}
              {item.value && <span className="truncate">— {item.value}</span>}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  )
}

// --------------

function ChatManagementResultCard({ result }: { result: ChatManagementNormalizedResult }) {
  const { t } = useTranslation()

  if (result.action === 'get_chats' && result.chats && result.chats.length > 0) {
    return (
      <Card className="border border-border/60 bg-muted/40 p-2">
        <div className="mb-1 flex items-center justify-between gap-2 text-xs">
          <div className="font-semibold">{t('Chats')}</div>
          <Badge variant="outline" className="border-border/60 text-[10px] uppercase">
            {result.chats.length}
          </Badge>
        </div>
        <ul className="space-y-1">
          {result.chats.map((chat, index) => (
            <li key={chat.id ?? index} className="flex items-center gap-2 text-xs">
              <span className="inline-flex size-4 items-center justify-center rounded border border-border/60 bg-background/60 text-[10px]">
                {index + 1}
              </span>
              <div className="min-w-0">
                <div className="truncate font-medium">{chat.name || t('Untitled chat')}</div>
                <div className="text-[10px] text-muted-foreground">
                  {chat.pinned && <span className="mr-1">{t('Pinned')}</span>}
                  {chat.model && <span>{chat.model}</span>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    )
  }

  if (result.action === 'get_messages' && result.messages && result.messages.length > 0) {
    return (
      <Card className="border border-border/60 bg-muted/40 p-2">
        <div className="mb-1 flex items-center justify-between gap-2 text-xs">
          <div className="font-semibold">{t('Messages')}</div>
          <Badge variant="outline" className="border-border/60 text-[10px] uppercase">
            {result.messages.length}
          </Badge>
        </div>
        <ul className="space-y-1">
          {result.messages.map((m, index) => (
            <li key={m.id ?? index} className="text-xs">
              <div className="truncate font-medium">{m.content || t('Message')}</div>
              <div className="text-[10px] text-muted-foreground">
                {m.role && <span className="mr-1">{m.role}</span>}
                {typeof m.thumbs_up === 'boolean' && (
                  <span>• {m.thumbs_up ? t('Thumbs up') : t('Thumbs down')}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    )
  }

  if (result.action === 'pin_chat' || result.action === 'star_message') {
    const label = result.action === 'pin_chat' ? t('Updated chat') : t('Updated feedback')
    return (
      <Card className="border border-border/60 bg-muted/40 p-2 text-xs">
        <div className="font-semibold">{label}</div>
      </Card>
    )
  }

  return (
    <Card className="border border-border/60 bg-muted/40 p-2 text-xs">
      <div className="font-semibold">{t('Chat management')}</div>
    </Card>
  )
}

// --------------

function PdfDocumentResultCard({ result }: { result: PdfDocumentNormalizedResult }) {
  const { t } = useTranslation()
  const url =
    result.url ||
    (result.pdfBase64 && !result.pdfBase64.startsWith('data:')
      ? `data:application/pdf;base64,${result.pdfBase64}`
      : result.pdfBase64)

  return (
    <Card className="my-1 w-full max-w-md border border-border/60 bg-muted/40 p-3">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-rose-500/15 text-rose-600 dark:text-rose-300">
          <FileText className="size-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <div className="truncate text-sm font-semibold">{result.fileName}</div>
            <div className="text-[11px] text-muted-foreground">
              {result.message || t('PDF ready to open')}
            </div>
            {result.error ? (
              <div className="text-[11px] text-rose-500">{result.error}</div>
            ) : null}
          </div>
          {url ? (
            <div className="flex flex-wrap gap-1.5">
              <Button asChild variant="secondary" size="sm" className="h-7 gap-1.5">
                <a href={url} target="_blank" rel="noreferrer">
                  {t('Open')} <ExternalLink className="size-3" />
                </a>
              </Button>
              <Button asChild variant="outline" size="sm" className="h-7 gap-1.5">
                <a href={url} download={result.fileName}>
                  {t('Download')} <Download className="size-3" />
                </a>
              </Button>
            </div>
          ) : (
            <div className="text-[11px] text-amber-600">
              {t('No download link — ask Losi to recreate the PDF.')}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

// --------------

function ConnectedServicesResultCard({ result }: { result: ConnectedServicesNormalizedResult }) {
  const { t } = useTranslation()

  const setup = result.setupCard
  const toolkitSlug = setup?.toolkit_slug || result.toolkitSlug
  const needsConnect =
    Boolean(setup) ||
    (result.operation === 'checkToolkit' && result.connected === false && Boolean(toolkitSlug))

  if (needsConnect && toolkitSlug) {
    const displayName =
      setup?.title?.replace(/^Connect\s+/i, '') || getComposioToolkitDisplayName(toolkitSlug)

    return (
      <div className="my-1 w-full max-w-md">
        <ExtensionConnectionCard
          extensionId={toolkitSlug}
          extensionName={displayName}
          description={
            setup?.description ||
            result.summary ||
            t('Connect this account so Losi can use it for you.')
          }
          icon={toolkitSlug}
          toolkitSlug={toolkitSlug}
          officialLogo={setup?.logo || `https://logos.composio.dev/api/${toolkitSlug}`}
        />
      </div>
    )
  }

  const opLabel =
    result.operation === 'listExtensions'
      ? t('Connected integrations')
      : result.operation === 'listActions'
        ? t('Available actions')
        : result.operation === 'executeAction'
          ? t('Executed action')
          : result.operation === 'checkToolkit'
            ? t('Connection status')
            : t('Connected integrations')

  return (
    <Card className="border border-border/60 bg-muted/40 p-2 text-xs">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="font-semibold">{opLabel}</div>
        {typeof result.connected === 'boolean' ? (
          <Badge
            variant="outline"
            className={cn(
              'border-border/60 text-[10px] uppercase',
              result.connected ? 'text-emerald-500' : 'text-amber-600'
            )}
          >
            {result.connected ? t('Connected') : t('Not connected')}
          </Badge>
        ) : typeof result.success === 'boolean' ? (
          <Badge
            variant="outline"
            className={cn(
              'border-border/60 text-[10px] uppercase',
              result.success ? 'text-emerald-500' : 'text-rose-500'
            )}
          >
            {result.success ? t('Success') : t('Error')}
          </Badge>
        ) : null}
      </div>
      {result.summary && (
        <div className="text-[11px] text-muted-foreground">{result.summary}</div>
      )}
      {result.error && <div className="text-[11px] text-rose-500">{result.error}</div>}
      {result.extensions && result.extensions.length > 0 && (
        <ul className="mt-1 space-y-1">
          {result.extensions.map((ext, index) => (
            <li key={ext.name ?? index} className="flex items-center gap-1">
              <span
                className={cn(
                  'size-1.5 rounded-full',
                  ext.active ? 'bg-emerald-500' : 'bg-muted-foreground/60'
                )}
              />
              <span className="truncate">{ext.name || t('Integration')}</span>
            </li>
          ))}
        </ul>
      )}
      {result.actions && result.actions.length > 0 && (
        <ul className="mt-1 space-y-1">
          {result.actions.map((act, index) => (
            <li key={act.id ?? index}>
              <div className="truncate font-medium">{act.name || act.id}</div>
              {act.description && (
                <div className="truncate text-[10px] text-muted-foreground">{act.description}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

// --------------

function ToolManagementResultCard({ result }: { result: ToolManagementNormalizedResult }) {
  const { t } = useTranslation()

  if (result.action === 'list' && result.tools && result.tools.length > 0) {
    return (
      <Card className="border border-border/60 bg-muted/40 p-2 text-xs">
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="font-semibold">{t('Listed tools')}</div>
          <span className="text-[10px] text-muted-foreground">{result.tools.length}</span>
        </div>
        <ul className="space-y-1">
          {result.tools.map((tool, index) => (
            <li key={tool.id ?? index}>
              <div className="flex items-center gap-1">
                <span className="truncate font-medium">{tool.name || t('Custom integration')}</span>
                {tool.badge && (
                  <Badge variant="outline" className="border-border/60 text-[9px] uppercase">
                    {tool.badge}
                  </Badge>
                )}
              </div>
              {tool.description && (
                <div className="text-[10px] text-muted-foreground">{tool.description}</div>
              )}
            </li>
          ))}
        </ul>
      </Card>
    )
  }

  if (result.action === 'enable' || result.action === 'disable') {
    const actionLabel = result.action === 'disable' ? t('disabled') : t('enabled')

    return (
      <Card className="border border-border/60 bg-muted/40 p-2 text-xs">
        <div className="font-semibold">
          {result.toolName ? `${result.toolName} ${actionLabel}` : result.label}
        </div>
      </Card>
    )
  }

  return (
    <Card className="border border-border/60 bg-muted/40 p-2 text-xs">
      <div className="font-semibold">{t('Managed tools')}</div>
    </Card>
  )
}

// --------------

function VectorMemoryResultCard({ result }: { result: VectorMemoryNormalizedResult }) {
  const { t } = useTranslation()

  return (
    <Card className="border border-border/60 bg-muted/40 p-2 text-xs">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="font-semibold">{t('Vector memory')}</div>
        {typeof result.count === 'number' && (
          <span className="text-[10px] text-muted-foreground">{result.count}</span>
        )}
      </div>
      {result.items && result.items.length > 0 && (
        <ul className="space-y-1">
          {result.items.map((item, index) => (
            <li key={item.key ?? index}>
              <div className="truncate font-mono text-[11px]">{item.key || t('Item')}</div>
              {item.content && (
                <div className="truncate text-[10px] text-muted-foreground">{item.content}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

// --------------

function SearchResultsCard({ result }: { result: SearchNormalizedResult }) {
  const { t } = useTranslation()

  const icon =
    result.kind === 'chat_search'
      ? MessageCircle
      : result.kind === 'bookmark_search'
        ? LinkIcon
        : MessageCircle

  const title =
    result.kind === 'chat_search'
      ? t('Chat search results')
      : result.kind === 'bookmark_search'
        ? t('Bookmark search results')
        : t('Starred messages')

  const Icon = icon

  return (
    <Card className="border border-border/60 bg-muted/40 p-2">
      <div className="mb-1 flex items-center gap-2 text-xs">
        <Icon className="size-3 text-muted-foreground" />
        <div className="font-semibold">{title}</div>
        <span className="text-[10px] text-muted-foreground">
          {result.items.length} {t('items')}
        </span>
      </div>
      <ul className="space-y-1">
        {result.items.map((item, index) => (
          <li key={item.id ?? `${item.chat_id}-${item.message_id}-${index}`} className="text-xs">
            <div className="truncate font-medium">{item.title || item.snippet || t('Message')}</div>
            {item.snippet && (
              <div className="truncate text-[10px] text-muted-foreground">{item.snippet}</div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  )
}

// --------------

/** Image/video tool body — preview only, no Result-summary chrome. */
function MediaGenerationCard({ result }: { result: MediaGenerationNormalizedResult }) {
  const { t } = useTranslation()
  const isVideo = result.kind === 'video_generation'

  if (result.items.length === 0) return null

  return (
    <div
      className={cn(
        'flex w-full gap-2',
        result.items.length > 1 ? 'flex-wrap' : 'flex-col'
      )}
    >
      {result.items.map((item, index) => {
        const safeUrl = getSafeRenderableImageUrl(item.thumbnailUrl || item.url)
        if (isVideo) {
          const videoSrc =
            typeof item.url === 'string' && item.url.trim().length > 0 ? item.url.trim() : null
          if (!videoSrc) {
            return (
              <button
                key={item.url ?? index}
                type="button"
                onClick={() => openInNewTab(item.url)}
                className="border-border/50 bg-muted/20 hover:bg-muted/40 flex min-w-[12rem] flex-1 items-center gap-2 rounded-xl border p-2 text-left text-xs transition-colors"
              >
                <PlayCircle className="text-muted-foreground size-4 shrink-0" />
                <span className="min-w-0 truncate font-medium">
                  {item.prompt || t('Video')}
                </span>
              </button>
            )
          }

          return (
            <div
              key={item.url ?? index}
              className={cn(
                'overflow-hidden rounded-xl',
                result.items.length > 1 ? 'min-w-[12rem] max-w-[24rem] flex-1' : 'w-full max-w-2xl'
              )}
            >
              <AnimatedMarkdownVideo
                src={videoSrc}
                alt={item.prompt || t('Generated video')}
                isStreaming={false}
              />
            </div>
          )
        }

        if (!safeUrl) {
          return (
            <button
              key={item.url ?? index}
              type="button"
              onClick={() => openInNewTab(item.url)}
              className="border-border/50 bg-muted/20 hover:bg-muted/40 flex min-w-[12rem] flex-1 items-center gap-2 rounded-xl border p-2 text-left text-xs transition-colors"
            >
              <FileImage className="text-muted-foreground size-4 shrink-0" />
              <span className="min-w-0 truncate font-medium">
                {item.prompt || t('Image')}
              </span>
            </button>
          )
        }

        return (
          <div
            key={item.url ?? index}
            className={cn(
              'overflow-hidden rounded-xl bg-transparent',
              result.items.length > 1 ? 'min-w-[10rem] max-w-[20rem] flex-1' : 'w-full max-w-xl'
            )}
          >
            <ImageWithPreview
              src={safeUrl}
              alt={item.prompt || t('Generated image')}
              className="my-0 max-h-[min(56dvh,520px)] w-full rounded-xl object-contain"
            />
          </div>
        )
      })}
    </div>
  )
}

// --------------

const SPACES_ENTITY_LABELS: Record<SpacesEntityType, { singular: string; plural: string }> = {
  item: { singular: 'Losi item', plural: 'Losi items' },
  space: { singular: 'Space', plural: 'Spaces' },
  task: { singular: 'Task', plural: 'Tasks' },
  note: { singular: 'Note', plural: 'Notes' },
  folder: { singular: 'Folder', plural: 'Folders' },
  chat: { singular: 'Chat', plural: 'Chats' },
  conversation: { singular: 'Conversation', plural: 'Conversations' },
  member: { singular: 'Member', plural: 'Members' },
  whiteboard: { singular: 'Whiteboard', plural: 'Whiteboards' },
  design: { singular: 'Design', plural: 'Designs' },
  sheet: { singular: 'Sheet', plural: 'Sheets' },
  sprint: { singular: 'Sprint', plural: 'Sprints' },
  event: { singular: 'Event', plural: 'Events' },
  file: { singular: 'File', plural: 'Files' },
  collection: { singular: 'Collection', plural: 'Collections' },
  calendar: { singular: 'Calendar', plural: 'Calendars' }
}

function formatEntityToken(value: string) {
  return value.replace(/[_-]+/g, ' ').trim()
}

function formatEntityDate(value?: string | null) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

function getPreviewText(value?: string) {
  if (!value) return ''

  return stripMarkdownForPreview(value.replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim()
}

function getSpacesEntityIcon(type: SpacesEntityType) {
  switch (type) {
    case 'item':
      return LinkIcon
    case 'space':
      return IconLayoutDashboard
    case 'task':
      return CheckSquare
    case 'sheet':
      return Table2
    case 'sprint':
      return Flag
    case 'event':
    case 'calendar':
      return CalendarDays
    case 'file':
      return Paperclip
    case 'collection':
      return Archive
    case 'folder':
      return FolderOpen
    case 'chat':
    case 'conversation':
      return MessageCircle
    case 'member':
      return Users
    case 'whiteboard':
    case 'design':
      return Palette
    default:
      return FileText
  }
}

function getPriorityBadgeClass(priority?: string) {
  switch (priority) {
    case 'urgent':
      return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200'
    case 'high':
      return 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900/50 dark:bg-orange-950/30 dark:text-orange-200'
    case 'medium':
      return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200'
    case 'low':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200'
    default:
      return 'border-border/60 bg-background/60'
  }
}

function SpacesEntityRow({ item }: { item: SpacesEntitySummary }) {
  const { t } = useTranslation()
  const Icon = getSpacesEntityIcon(item.type)
  const preview = getPreviewText(item.description)
  const dueDate = formatEntityDate(item.dueDate)
  const updatedAt = formatEntityDate(item.updatedAt)
  const createdAt = formatEntityDate(item.createdAt)
  const Wrapper = item.url ? 'a' : 'div'
  const wrapperProps = item.url ? { href: item.url, title: item.url } : {}

  return (
    <Wrapper
      {...wrapperProps}
      className={cn(
        'flex items-start gap-2 rounded-md border border-border/60 bg-background/70 p-2 text-left text-xs transition-colors',
        item.url && 'hover:border-border hover:bg-muted/60'
      )}
    >
      <div className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/40 text-muted-foreground">
        <Icon className="size-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate font-medium">
              {item.title || SPACES_ENTITY_LABELS[item.type].singular}
            </div>
            {preview && <div className="mt-0.5 line-clamp-2 text-muted-foreground">{preview}</div>}
          </div>
          {item.url && <ExternalLink className="size-3 shrink-0 text-muted-foreground" />}
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {item.status && (
            <Badge variant="outline" className="border-border/60 text-[10px]">
              {formatEntityToken(item.status)}
            </Badge>
          )}
          {item.priority && (
            <Badge
              variant="outline"
              className={cn('text-[10px] capitalize', getPriorityBadgeClass(item.priority))}
            >
              {item.priority}
            </Badge>
          )}
          {item.role && (
            <Badge variant="outline" className="border-border/60 text-[10px]">
              {formatEntityToken(item.role)}
            </Badge>
          )}
          {item.sharing && (
            <Badge variant="outline" className="border-border/60 text-[10px]">
              {formatEntityToken(item.sharing)}
            </Badge>
          )}
          {item.contextType && (
            <Badge variant="outline" className="border-border/60 text-[10px]">
              {formatEntityToken(item.contextType)}
            </Badge>
          )}
          {item.model && (
            <Badge variant="outline" className="border-border/60 text-[10px]">
              {item.model}
            </Badge>
          )}
          {item.locked && (
            <Badge variant="outline" className="border-border/60 text-[10px]">
              {t('Locked')}
            </Badge>
          )}
          {item.archived && (
            <Badge variant="outline" className="border-border/60 text-[10px]">
              {t('Archived')}
            </Badge>
          )}
          {item.favorited && (
            <Badge variant="outline" className="border-border/60 text-[10px]">
              {t('Favorited')}
            </Badge>
          )}
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
          {item.email && <span>{item.email}</span>}
          {dueDate && (
            <span>
              {t('Due')} {dueDate}
            </span>
          )}
          {!dueDate && updatedAt && (
            <span>
              {t('Updated')} {updatedAt}
            </span>
          )}
          {!dueDate && !updatedAt && createdAt && (
            <span>
              {t('Created')} {createdAt}
            </span>
          )}
        </div>
      </div>
    </Wrapper>
  )
}

function SpacesEntityResultCard({ result }: { result: SpacesEntityNormalizedResult }) {
  const { t } = useTranslation()
  const Icon = getSpacesEntityIcon(result.entityType)
  const headerTitle =
    result.label ||
    (result.kind === 'spaces_entity'
      ? SPACES_ENTITY_LABELS[result.entityType].singular
      : SPACES_ENTITY_LABELS[result.entityType].plural)

  return (
    <Card className="border border-border/60 bg-muted/40 p-2">
      <div className="mb-2 flex items-center justify-between gap-2 text-xs">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="size-3.5 shrink-0 text-muted-foreground" />
          <div className="truncate font-semibold">{t(headerTitle)}</div>
        </div>
        <Badge variant="outline" className="border-border/60 text-[10px] uppercase">
          {result.items.length}
        </Badge>
      </div>
      <div className="space-y-2">
        {result.items.map((item, index) => (
          <SpacesEntityRow key={item.id ?? `${item.type}-${index}`} item={item} />
        ))}
      </div>
    </Card>
  )
}

function SpacesTaskSupportCard({ result }: { result: SpacesTaskSupportNormalizedResult }) {
  const { t } = useTranslation()
  const title =
    result.label || (result.supportType === 'checklist' ? t('Checklist') : t('Subtasks'))

  return (
    <Card className="border border-border/60 bg-muted/40 p-2">
      <div className="mb-2 flex items-center justify-between gap-2 text-xs">
        <div className="font-semibold">{title}</div>
        <Badge variant="outline" className="border-border/60 text-[10px] uppercase">
          {result.items.length}
        </Badge>
      </div>
      <ul className="space-y-1.5">
        {result.items.map((item, index) => (
          <li
            key={item.id ?? `${result.supportType}-${index}`}
            className="flex items-start gap-2 rounded-md border border-border/60 bg-background/70 p-2 text-xs"
          >
            <span
              className={cn(
                'mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full border text-[10px]',
                item.completed
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-border/60 bg-background/60 text-muted-foreground'
              )}
            >
              {item.completed ? '✓' : index + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{item.title || t('Untitled item')}</div>
              {item.description && (
                <div className="mt-0.5 line-clamp-2 text-muted-foreground">
                  {getPreviewText(item.description)}
                </div>
              )}
              {item.dueDate && (
                <div className="mt-1 text-[10px] text-muted-foreground">
                  {t('Due')} {formatEntityDate(item.dueDate)}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function SpacesConversationMessagesCard({
  result
}: {
  result: SpacesConversationMessagesNormalizedResult
}) {
  const { t } = useTranslation()

  return (
    <Card className="border border-border/60 bg-muted/40 p-2">
      <div className="mb-2 flex items-center justify-between gap-2 text-xs">
        <div className="min-w-0">
          <div className="font-semibold">
            {result.label || result.conversation?.title || t('Conversation messages')}
          </div>
          {result.conversation?.contextType && (
            <div className="text-[10px] text-muted-foreground">
              {formatEntityToken(result.conversation.contextType)}
            </div>
          )}
        </div>
        <Badge variant="outline" className="border-border/60 text-[10px] uppercase">
          {result.messages.length}
        </Badge>
      </div>
      <div className="space-y-2">
        {result.messages.map((message, index) => (
          <div
            key={message.id ?? `message-${index}`}
            className="rounded-md border border-border/60 bg-background/70 p-2 text-xs"
          >
            <div className="mb-1 flex flex-wrap items-center gap-1.5">
              {message.role && (
                <Badge variant="outline" className="border-border/60 text-[10px] uppercase">
                  {formatEntityToken(message.role)}
                </Badge>
              )}
              {message.authorName && (
                <span className="text-[10px] text-muted-foreground">{message.authorName}</span>
              )}
              {message.model && (
                <span className="text-[10px] text-muted-foreground">{message.model}</span>
              )}
              {message.createdAt && (
                <span className="text-[10px] text-muted-foreground">
                  {formatEntityDate(message.createdAt)}
                </span>
              )}
            </div>
            {message.content && (
              <div className="whitespace-pre-wrap break-words">
                {getPreviewText(message.content) || message.content}
              </div>
            )}
            {message.errorMessage && (
              <div className="mt-1 text-[10px] text-rose-500">{message.errorMessage}</div>
            )}
          </div>
        ))}
      </div>
    </Card>
  )
}

function SpacesInvitationCard({ result }: { result: SpacesInvitationNormalizedResult }) {
  const { t } = useTranslation()
  const hasFailure =
    (result.emailsFailed?.length ?? 0) > 0 || (result.validationErrors?.length ?? 0) > 0

  return (
    <Card className="border border-border/60 bg-muted/40 p-2 text-xs">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="font-semibold">{t('Space invitation')}</div>
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] uppercase',
            hasFailure
              ? 'border-amber-300 text-amber-700 dark:border-amber-900/50 dark:text-amber-200'
              : 'border-emerald-300 text-emerald-700 dark:border-emerald-900/50 dark:text-emerald-200'
          )}
        >
          {hasFailure ? t('Review') : t('Sent')}
        </Badge>
      </div>
      {result.message && <div>{result.message}</div>}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {typeof result.createdCount === 'number' && (
          <Badge variant="outline" className="border-border/60 text-[10px]">
            {result.createdCount} {t('created')}
          </Badge>
        )}
        {typeof result.requestedCount === 'number' && (
          <Badge variant="outline" className="border-border/60 text-[10px]">
            {result.requestedCount} {t('requested')}
          </Badge>
        )}
        {(result.emailsSent?.length ?? 0) > 0 && (
          <Badge
            variant="outline"
            className="border-emerald-300 text-[10px] text-emerald-700 dark:border-emerald-900/50 dark:text-emerald-200"
          >
            {result.emailsSent?.length} {t('email sent')}
          </Badge>
        )}
        {(result.emailsFailed?.length ?? 0) > 0 && (
          <Badge
            variant="outline"
            className="border-rose-300 text-[10px] text-rose-700 dark:border-rose-900/50 dark:text-rose-200"
          >
            {result.emailsFailed?.length} {t('email failed')}
          </Badge>
        )}
      </div>
      {(result.emailsSent?.length ?? 0) > 0 && (
        <div className="mt-2 text-[10px] text-muted-foreground">
          {result.emailsSent?.join(', ')}
        </div>
      )}
      {(result.validationErrors?.length ?? 0) > 0 && (
        <ul className="mt-2 space-y-1 text-[10px] text-amber-600 dark:text-amber-300">
          {result.validationErrors?.map((error, index) => (
            <li key={`${error}-${index}`}>{error}</li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function SpacesStatusCard({ result }: { result: SpacesStatusNormalizedResult }) {
  const { t } = useTranslation()
  const Icon =
    result.state === 'loading'
      ? Loader2
      : result.state === 'error'
        ? AlertCircle
        : result.state === 'success'
          ? CheckCircle2
          : FileText

  const toneClass =
    result.state === 'error'
      ? 'border-rose-200/80 bg-rose-50/70 dark:border-rose-900/40 dark:bg-rose-950/20'
      : result.state === 'success'
        ? 'border-emerald-200/80 bg-emerald-50/70 dark:border-emerald-900/40 dark:bg-emerald-950/20'
        : 'border-border/60 bg-muted/40'

  return (
    <Card className={cn('border p-2 text-xs', toneClass)}>
      <div className="flex items-start gap-2">
        <Icon
          className={cn(
            'mt-0.5 size-4 shrink-0',
            result.state === 'loading' && 'motion-safe:animate-spin motion-reduce:animate-none',
            result.state === 'error'
              ? 'text-rose-500'
              : result.state === 'success'
                ? 'text-emerald-500'
                : 'text-muted-foreground'
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{result.title}</div>
          {result.description && (
            <div className="mt-0.5 text-muted-foreground">{result.description}</div>
          )}
          {result.detailLines && result.detailLines.length > 0 && (
            <ul className="mt-2 space-y-1 text-[10px] text-muted-foreground">
              {result.detailLines.map((line, index) => (
                <li key={`${line}-${index}`}>{line}</li>
              ))}
            </ul>
          )}
          {result.state === 'loading' && (
            <div className="mt-1 text-[10px] text-muted-foreground">
              {t('Waiting for Spaces to finish this action')}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

// --------------

function GenericResultCard({ result }: { result: NormalizedToolResult }) {
  const raw = 'raw' in result ? result.raw : result

  return <ToolPayloadSummary result={raw} />
}

// --------------

function renderCardForNormalized(result: NormalizedToolResult): React.ReactNode {
  switch (result.kind) {
    case 'web_search':
      return <WebSearchResultCard result={result} />
    case 'fetched_url':
      return <FetchedUrlResultCard result={result as FetchedUrlNormalizedResult} />
    case 'weather':
      return <WeatherResultCard result={result as WeatherNormalizedResult} />
    case 'finance_quote':
      return <FinanceResultCard result={result as FinanceQuoteNormalizedResult} />
    case 'virtual_file_single':
    case 'virtual_file_list':
      return (
        <VirtualFileResultCard
          result={result as VirtualFileSingleNormalizedResult | VirtualFileListNormalizedResult}
        />
      )
    case 'task':
    case 'task_list':
      return <TaskResultCard result={result as TaskNormalizedResult} />
    case 'memory':
      return <MemoryResultCard result={result as MemoryNormalizedResult} />
    case 'chat_management':
      return <ChatManagementResultCard result={result as ChatManagementNormalizedResult} />
    case 'pdf_document':
      return <PdfDocumentResultCard result={result as PdfDocumentNormalizedResult} />
    case 'connected_services':
      return <ConnectedServicesResultCard result={result as ConnectedServicesNormalizedResult} />
    case 'tool_management':
      return <ToolManagementResultCard result={result as ToolManagementNormalizedResult} />
    case 'vector_memory':
      return <VectorMemoryResultCard result={result as VectorMemoryNormalizedResult} />
    case 'chat_search':
    case 'bookmark_search':
    case 'starred_search':
      return <SearchResultsCard result={result as SearchNormalizedResult} />
    case 'image_generation':
    case 'video_generation':
      return <MediaGenerationCard result={result as MediaGenerationNormalizedResult} />
    case 'sidecar_run': {
      const sr = result as SidecarRunNormalizedResult
      const annotation = {
        sidecar_run: {
          runId: sr.runId,
          status: sr.status,
          objective: sr.objective,
          reason: sr.reason,
          finalAnswer: sr.finalAnswer ?? sr.summary,
          agentCount: sr.agentCount,
          successfulAgents: sr.successfulAgents,
          failedAgents: sr.failedAgents,
          plannedWorkers: sr.plannedWorkers,
          workers: sr.workers
        }
      }
      return <SidecarRunAnnotation annotation={annotation} />
    }
    case 'spaces_entity':
    case 'spaces_entity_list':
      return <SpacesEntityResultCard result={result as SpacesEntityNormalizedResult} />
    case 'spaces_task_support':
      return <SpacesTaskSupportCard result={result as SpacesTaskSupportNormalizedResult} />
    case 'spaces_conversation_messages':
      return (
        <SpacesConversationMessagesCard
          result={result as SpacesConversationMessagesNormalizedResult}
        />
      )
    case 'spaces_invitation':
      return <SpacesInvitationCard result={result as SpacesInvitationNormalizedResult} />
    case 'spaces_status':
      return <SpacesStatusCard result={result as SpacesStatusNormalizedResult} />
    case 'generic':
    default:
      return <GenericResultCard result={result} />
  }
}

// --------------

function SoftGateResultCard({ entry }: { entry: ToolActivityEntry }) {
  const record = parseRecordResult(entry.result) ?? {}
  const code =
    typeof record.code === 'string' && record.code.trim()
      ? record.code.trim()
      : null
  const message =
    (typeof record.message === 'string' && record.message.trim()
      ? record.message.trim()
      : null) ||
    (typeof record.error === 'string' && record.error.trim()
      ? record.error.trim()
      : null) ||
    (typeof entry.result === 'string' && entry.result.trim()
      ? entry.result.trim()
      : null) ||
    'This action needs you before it can finish.'

  const title =
    code === 'APPROVAL_REQUIRED' || record.requiresConfirmation === true
      ? 'Needs your approval'
      : record.requires_confirmation === true
        ? 'Needs your confirmation'
        : code === 'CONFIRMATION_REQUIRED'
          ? 'Confirm to continue'
          : 'Waiting on you'

  return (
    <Card className="border-0 bg-amber-500/10 p-3 shadow-none">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-300">
          <Hand className="size-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-semibold">{title}</div>
            <Badge variant="outline">Needs you</Badge>
            {code ? <Badge variant="secondary">{code.replace(/_/g, ' ')}</Badge> : null}
          </div>
          <p className="text-muted-foreground text-xs">{message}</p>
          <p className="text-muted-foreground text-[11px]">
            Approve, confirm, connect, or upgrade in chat, then ask me to continue. This is not a
            hard failure.
          </p>
        </div>
      </div>
    </Card>
  )
}

export function renderToolResultForEntry(entry: ToolActivityEntry): React.ReactNode {
  try {
    const sandboxResult = parseRecordResult(entry.result)
    if (isSandboxToolEntry(entry, sandboxResult)) {
      return <SandboxResultCard entry={entry} />
    }

    if (entry.key.startsWith('liveScores__')) {
      const normalized = parseRecordResult(entry.result) ?? entry.result
      return (
        <LiveScoreAnnotation
          annotation={{ [entry.key]: normalized } as Annotation}
          toolKey={entry.key}
        />
      )
    }

    if (entry.key === 'rename_chat') {
      return <RenameChatResultCard result={entry.result} />
    }

    if (entry.key === 'set_todos' || entry.key === 'update_todo_status') {
      return null
    }

    if (entry.key === 'GeneratedIntegrationTool__draftIntegration') {
      const draft = getGeneratedIntegrationDraft(entry.result)

      return draft ? <GeneratedIntegrationApprovalCard draft={draft} /> : null
    }

    if (entry.key === 'MCPIntegrationTool__installMcpServer') {
      return <McpInstallResultCard result={entry.result} />
    }

    if (entry.key.startsWith('MCPIntegrationTool__')) {
      return <McpRuntimeResultCard entry={entry} />
    }

    if (isSkillInstallToolKey(entry.key)) {
      return <SkillInstallResultCard result={entry.result} />
    }

    if (entry.key.startsWith('SkillIntegrationTool__')) {
      return <SkillRuntimeResultCard entry={entry} />
    }

    if (entry.key.startsWith('ContentEngine__')) {
      return <ContentEngineResultCard entry={entry} />
    }

    if (
      entry.key === 'GeneratedIntegrationTool__approveIntegrationDraft' ||
      entry.key === 'GeneratedIntegrationTool__updateIntegration' ||
      entry.key === 'GeneratedIntegrationTool__deleteIntegration'
    ) {
      return <GeneratedIntegrationCreatedCard result={entry.result} />
    }

    // Generic soft-gate (approval / booking confirm / Composio) after specialized cards.
    if (isSoftGateToolResult(entry.result)) {
      return <SoftGateResultCard entry={entry} />
    }

    const normalized = normalizeToolResult(entry.key, entry.result)
    return renderCardForNormalized(normalized)
  } catch (error) {
    if (entry.key.startsWith('losiSpaces__')) {
      return (
        <SpacesStatusCard
          result={{
            kind: 'spaces_status',
            key: entry.key,
            label: 'Spaces',
            state: 'error',
            title: 'Spaces result unavailable',
            description: 'Losi could not render this Spaces result safely.'
          }}
        />
      )
    }

    // If normalisation fails, fall back to existing JSON preview
    console.error('Error normalizing tool result', error)
    return null
  }
}
